import { initialApi as api } from './initialApi';
export const addTagTypes = [
  'peripheral-order-controller',
  'quickpay-controller',
  'peripheral-license-redeem-controller',
  'partner-invoice-controller',
  'migration-ovi-controller',
  'billing-controller',
  'discount-controller',
  'zuora-webhook-controller',
  'webhook-controller',
  'payment-controller',
  'invoice-controller',
  'admin-controller',
  'pre-prod-admin-controller',
  'asset-license-controller',
  'license-controller',
  'entitlements-controller',
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      updateShippingAddress: build.mutation<
        UpdateShippingAddressApiResponse,
        UpdateShippingAddressApiArg
      >({
        query: (queryArg) => ({
          url: `/peripheral/2026-05-05/organizations/${queryArg.orgId}/shipping-addresses/${queryArg.shippingAddressId}`,
          method: 'PUT',
          body: queryArg.saveShippingAddressRequestDto,
        }),
        invalidatesTags: ['peripheral-order-controller'],
      }),
      createPaymentSession: build.mutation<
        CreatePaymentSessionApiResponse,
        CreatePaymentSessionApiArg
      >({
        query: (queryArg) => ({
          url: '/quickpay/2026-05-05/payments/sessions',
          method: 'POST',
          body: queryArg.createPaymentSessionRequest,
        }),
        invalidatesTags: ['quickpay-controller'],
      }),
      submitQuickpayEmail: build.mutation<
        SubmitQuickpayEmailApiResponse,
        SubmitQuickpayEmailApiArg
      >({
        query: (queryArg) => ({
          url: '/quickpay/2026-05-05/email',
          method: 'POST',
          body: queryArg.submitQuickpayEmailRequest,
        }),
        invalidatesTags: ['quickpay-controller'],
      }),
      createShippingAddress: build.mutation<
        CreateShippingAddressApiResponse,
        CreateShippingAddressApiArg
      >({
        query: (queryArg) => ({
          url: `/peripheral/2026-05-05/organizations/${queryArg.orgId}/shipping-addresses`,
          method: 'POST',
          body: queryArg.saveShippingAddressRequestDto,
        }),
        invalidatesTags: ['peripheral-order-controller'],
      }),
      listOrders: build.query<ListOrdersApiResponse, ListOrdersApiArg>({
        query: (queryArg) => ({
          url: `/peripheral/2026-05-05/organizations/${queryArg.orgId}/orders`,
          params: {
            searchBy: queryArg.searchBy,
            pageSize: queryArg.pageSize,
          },
        }),
        providesTags: ['peripheral-order-controller'],
      }),
      placeOrder: build.mutation<PlaceOrderApiResponse, PlaceOrderApiArg>({
        query: (queryArg) => ({
          url: `/peripheral/2026-05-05/organizations/${queryArg.orgId}/orders`,
          method: 'POST',
          body: queryArg.placePeripheralOrderDto,
        }),
        invalidatesTags: ['peripheral-order-controller'],
      }),
      previewPeripheralOrder: build.query<
        PreviewPeripheralOrderApiResponse,
        PreviewPeripheralOrderApiArg
      >({
        query: (queryArg) => ({
          url: `/peripheral/2026-05-05/organizations/${queryArg.orgId}/orders/preview`,
          method: 'POST',
          body: queryArg.previewPeripheralOrderDto,
        }),
        providesTags: ['peripheral-order-controller'],
      }),
      redeemLicense: build.mutation<
        RedeemLicenseApiResponse,
        RedeemLicenseApiArg
      >({
        query: (queryArg) => ({
          url: `/peripheral/2026-05-05/organizations/${queryArg.orgId}/license/redeem`,
          method: 'POST',
          body: queryArg.redeemLicenseRequestDto,
        }),
        invalidatesTags: ['peripheral-license-redeem-controller'],
      }),
      listInvoices: build.mutation<ListInvoicesApiResponse, ListInvoicesApiArg>(
        {
          query: (queryArg) => ({
            url: '/partner/2026-09-22/organizations/invoices/list',
            method: 'POST',
            body: queryArg.partnerInvoiceListRequest,
          }),
          invalidatesTags: ['partner-invoice-controller'],
        }
      ),
      syncOviSubscriptions: build.mutation<
        SyncOviSubscriptionsApiResponse,
        SyncOviSubscriptionsApiArg
      >({
        query: (queryArg) => ({
          url: '/migration/2026-08-25/ovi/orders',
          method: 'POST',
          body: queryArg.syncOviSubscriptionsRequest,
        }),
        invalidatesTags: ['migration-ovi-controller'],
      }),
      validateMigratedSubscriptions: build.mutation<
        ValidateMigratedSubscriptionsApiResponse,
        ValidateMigratedSubscriptionsApiArg
      >({
        query: (queryArg) => ({
          url: '/migration/2026-08-25/ovi/orders/validate',
          method: 'POST',
          body: queryArg.syncOviSubscriptionsRequest,
        }),
        invalidatesTags: ['migration-ovi-controller'],
      }),
      updateZuoraAccount: build.mutation<
        UpdateZuoraAccountApiResponse,
        UpdateZuoraAccountApiArg
      >({
        query: (queryArg) => ({
          url: '/migration/2026-08-25/ovi/final/accounts',
          method: 'POST',
          body: queryArg.updateZuoraAccountRequest,
        }),
        invalidatesTags: ['migration-ovi-controller'],
      }),
      validateZuoraAccount: build.mutation<
        ValidateZuoraAccountApiResponse,
        ValidateZuoraAccountApiArg
      >({
        query: (queryArg) => ({
          url: '/migration/2026-08-25/ovi/final/accounts/validate',
          method: 'POST',
          body: queryArg.validateZuoraAccountRequest,
        }),
        invalidatesTags: ['migration-ovi-controller'],
      }),
      findMigratedBillingAccounts: build.query<
        FindMigratedBillingAccountsApiResponse,
        FindMigratedBillingAccountsApiArg
      >({
        query: (queryArg) => ({
          url: '/migration/2026-08-25/ovi/accounts',
          params: {
            accountId: queryArg.accountId,
            organizationId: queryArg.organizationId,
          },
        }),
        providesTags: ['migration-ovi-controller'],
      }),
      syncOviBillingAccount: build.mutation<
        SyncOviBillingAccountApiResponse,
        SyncOviBillingAccountApiArg
      >({
        query: (queryArg) => ({
          url: '/migration/2026-08-25/ovi/accounts',
          method: 'POST',
          body: queryArg.syncOviBillingAccountRequest,
          headers: {
            'FORCE-CHANGE': queryArg['FORCE-CHANGE'],
          },
        }),
        invalidatesTags: ['migration-ovi-controller'],
      }),
      validateMigratedOviBillingAccount: build.mutation<
        ValidateMigratedOviBillingAccountApiResponse,
        ValidateMigratedOviBillingAccountApiArg
      >({
        query: (queryArg) => ({
          url: '/migration/2026-08-25/ovi/accounts/validate',
          method: 'POST',
          body: queryArg.validateMigratedOviBillingAccountRequest,
        }),
        invalidatesTags: ['migration-ovi-controller'],
      }),
      updateInvoiceMeEnabledIfAllowed: build.mutation<
        UpdateInvoiceMeEnabledIfAllowedApiResponse,
        UpdateInvoiceMeEnabledIfAllowedApiArg
      >({
        query: (queryArg) => ({
          url: '/billing/2026-05-19/payment-methods/invoice-me',
          method: 'POST',
          body: queryArg.updateInvoiceMeEnabledRequest,
        }),
        invalidatesTags: ['billing-controller'],
      }),
      applyDiscountToSubscription: build.mutation<
        ApplyDiscountToSubscriptionApiResponse,
        ApplyDiscountToSubscriptionApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2026-02-10/organizations/${queryArg.orgId}/subscriptions/${queryArg.subscriptionNumber}/discount`,
          method: 'POST',
          body: queryArg.discountRequestDto,
        }),
        invalidatesTags: ['discount-controller'],
      }),
      listDiscounts: build.mutation<
        ListDiscountsApiResponse,
        ListDiscountsApiArg
      >({
        query: (queryArg) => ({
          url: '/billing/2026-02-10/discounts',
          method: 'POST',
          body: queryArg.listDiscountsRequest,
        }),
        invalidatesTags: ['discount-controller'],
      }),
      exportDiscounts: build.mutation<
        ExportDiscountsApiResponse,
        ExportDiscountsApiArg
      >({
        query: (queryArg) => ({
          url: '/billing/2026-02-10/discounts/export',
          method: 'POST',
          body: queryArg.discountFilter,
          params: {
            locale: queryArg.locale,
            timeZone: queryArg.timeZone,
          },
        }),
        invalidatesTags: ['discount-controller'],
      }),
      handleZuoraCallout: build.mutation<
        HandleZuoraCalloutApiResponse,
        HandleZuoraCalloutApiArg
      >({
        query: (queryArg) => ({
          url: '/billing/2025-09-23/webhooks',
          method: 'POST',
          body: queryArg.zuoraMessage,
        }),
        invalidatesTags: ['zuora-webhook-controller'],
      }),
      handleInvoicePosted: build.mutation<
        HandleInvoicePostedApiResponse,
        HandleInvoicePostedApiArg
      >({
        query: (queryArg) => ({
          url: '/billing/2025-06-11/webhooks/invoice-posted',
          method: 'POST',
          body: queryArg.invoicePostedDto,
        }),
        invalidatesTags: ['webhook-controller'],
      }),
      createPaymentSession1: build.mutation<
        CreatePaymentSession1ApiResponse,
        CreatePaymentSession1ApiArg
      >({
        query: (queryArg) => ({
          url: '/billing/2025-06-11/payment-methods/sessions',
          method: 'POST',
          body: queryArg.paymentSessionRequestDto,
        }),
        invalidatesTags: ['payment-controller'],
      }),
      addPaymentMethod: build.mutation<
        AddPaymentMethodApiResponse,
        AddPaymentMethodApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2025-06-11/organizations/${queryArg.orgId}/payment-methods`,
          method: 'POST',
          body: queryArg.addPaymentMethodDto,
        }),
        invalidatesTags: ['payment-controller'],
      }),
      getInvoiceHistory: build.query<
        GetInvoiceHistoryApiResponse,
        GetInvoiceHistoryApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2025-06-11/organizations/${queryArg.orgId}/invoices`,
          method: 'POST',
          body: queryArg.invoiceHistoryRequestDto,
        }),
        providesTags: ['invoice-controller'],
      }),
      downloadInvoices: build.mutation<
        DownloadInvoicesApiResponse,
        DownloadInvoicesApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2025-06-11/organizations/${queryArg.orgId}/invoices/files`,
          method: 'POST',
          body: queryArg.invoiceDownloadRequestDto,
        }),
        invalidatesTags: ['invoice-controller'],
      }),
      createOrder: build.mutation<CreateOrderApiResponse, CreateOrderApiArg>({
        query: (queryArg) => ({
          url: '/billing/2025-06-11/orders',
          method: 'POST',
          body: queryArg.createOrderRequestDto,
        }),
        invalidatesTags: ['billing-controller'],
      }),
      unenrollAssets: build.mutation<
        UnenrollAssetsApiResponse,
        UnenrollAssetsApiArg
      >({
        query: (queryArg) => ({
          url: '/billing/2025-06-11/orders/unenroll',
          method: 'POST',
          body: queryArg.requestUnenrollAssetsDto,
        }),
        invalidatesTags: ['billing-controller'],
      }),
      previewOrder: build.query<PreviewOrderApiResponse, PreviewOrderApiArg>({
        query: (queryArg) => ({
          url: '/billing/2025-06-11/orders/preview',
          method: 'POST',
          body: queryArg.orderPreviewRequestDto,
        }),
        providesTags: ['billing-controller'],
      }),
      enrollAssets: build.mutation<EnrollAssetsApiResponse, EnrollAssetsApiArg>(
        {
          query: (queryArg) => ({
            url: '/billing/2025-06-11/orders/enroll',
            method: 'POST',
            body: queryArg.requestEnrollAssetsDto,
          }),
          invalidatesTags: ['billing-controller'],
        }
      ),
      previewEnrollAssets: build.query<
        PreviewEnrollAssetsApiResponse,
        PreviewEnrollAssetsApiArg
      >({
        query: (queryArg) => ({
          url: '/billing/2025-06-11/orders/enroll/preview',
          method: 'POST',
          body: queryArg.previewEnrollAssetsDto,
        }),
        providesTags: ['billing-controller'],
      }),
      executeActions: build.mutation<
        ExecuteActionsApiResponse,
        ExecuteActionsApiArg
      >({
        query: (queryArg) => ({
          url: '/admin/2024-06-01/commands',
          method: 'POST',
          body: queryArg.commandDto,
        }),
        invalidatesTags: ['admin-controller'],
      }),
      cloneAndOnboard: build.mutation<
        CloneAndOnboardApiResponse,
        CloneAndOnboardApiArg
      >({
        query: (queryArg) => ({
          url: '/admin/2024-06-01/cloneAndOnboard',
          method: 'POST',
          body: queryArg.cloneAndOnboardDto,
        }),
        invalidatesTags: ['pre-prod-admin-controller'],
      }),
      getOrganizationAssetsWithPrice: build.query<
        GetOrganizationAssetsWithPriceApiResponse,
        GetOrganizationAssetsWithPriceApiArg
      >({
        query: (queryArg) => ({
          url: `/2026-02-24/organizations/${queryArg.organizationId}/assets-with-price`,
          method: 'POST',
          body: queryArg.assetsWithPricingRequest,
        }),
        providesTags: ['asset-license-controller'],
      }),
      getOrganizationAssets: build.query<
        GetOrganizationAssetsApiResponse,
        GetOrganizationAssetsApiArg
      >({
        query: (queryArg) => ({
          url: `/2025-06-11/organizations/${queryArg.organizationId}/assets`,
          method: 'POST',
          body: queryArg.listOrganizationAssets,
        }),
        providesTags: ['asset-license-controller'],
      }),
      getLicenses: build.query<GetLicensesApiResponse, GetLicensesApiArg>({
        query: (queryArg) => ({
          url: `/2024-06-01/organizations/${queryArg.orgId}/licenses`,
          method: 'POST',
          body: queryArg.getLicenseListDto,
        }),
        providesTags: ['license-controller'],
      }),
      createLicense: build.mutation<
        CreateLicenseApiResponse,
        CreateLicenseApiArg
      >({
        query: (queryArg) => ({
          url: `/2024-06-01/organizations/${queryArg.orgId}/licenses/create`,
          method: 'POST',
          body: queryArg.createLicenseDto,
        }),
        invalidatesTags: ['license-controller'],
      }),
      getOrganizationEnablements: build.query<
        GetOrganizationEnablementsApiResponse,
        GetOrganizationEnablementsApiArg
      >({
        query: (queryArg) => ({
          url: `/2024-06-01/organizations/${queryArg.orgId}/enablements`,
          method: 'POST',
          body: queryArg.enablementsFiltersDto,
        }),
        providesTags: ['entitlements-controller'],
      }),
      reprocessLicensesForAssets: build.mutation<
        ReprocessLicensesForAssetsApiResponse,
        ReprocessLicensesForAssetsApiArg
      >({
        query: (queryArg) => ({
          url: `/2024-06-01/organizations/${queryArg.orgId}/batch`,
          method: 'POST',
          body: queryArg.reprocessingDto,
        }),
        invalidatesTags: ['entitlements-controller'],
      }),
      cancelDiscount: build.mutation<
        CancelDiscountApiResponse,
        CancelDiscountApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2026-02-10/organizations/${queryArg.orgId}/discounts/${queryArg.discountId}`,
          method: 'DELETE',
          params: {
            cancellationReason: queryArg.cancellationReason,
          },
        }),
        invalidatesTags: ['discount-controller'],
      }),
      changeDiscount: build.mutation<
        ChangeDiscountApiResponse,
        ChangeDiscountApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2026-02-10/organizations/${queryArg.orgId}/discounts/${queryArg.discountId}`,
          method: 'PATCH',
          body: queryArg.discountRequestDto,
        }),
        invalidatesTags: ['discount-controller'],
      }),
      setDefaultPaymentMethod: build.mutation<
        SetDefaultPaymentMethodApiResponse,
        SetDefaultPaymentMethodApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2026-01-27/organizations/${queryArg.orgId}/payment-methods/${queryArg.paymentMethodId}/set-default`,
          method: 'PATCH',
        }),
        invalidatesTags: ['payment-controller'],
      }),
      updatePaymentMethod: build.mutation<
        UpdatePaymentMethodApiResponse,
        UpdatePaymentMethodApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2025-06-11/organizations/${queryArg.orgId}/payment-methods/${queryArg.paymentMethodId}`,
          method: 'PATCH',
          body: queryArg.updatePaymentMethodDto,
        }),
        invalidatesTags: ['payment-controller'],
      }),
      updateLicense: build.mutation<
        UpdateLicenseApiResponse,
        UpdateLicenseApiArg
      >({
        query: (queryArg) => ({
          url: `/2024-06-01/organizations/${queryArg.orgId}/licenses/${queryArg.licenseId}`,
          method: 'PATCH',
          body: queryArg.updateLicenseDto,
        }),
        invalidatesTags: ['license-controller'],
      }),
      getPaymentDetails: build.query<
        GetPaymentDetailsApiResponse,
        GetPaymentDetailsApiArg
      >({
        query: (queryArg) => ({
          url: `/quickpay/2026-05-05/payments/${queryArg.paymentId}`,
        }),
        providesTags: ['quickpay-controller'],
      }),
      searchAccountInvoice: build.query<
        SearchAccountInvoiceApiResponse,
        SearchAccountInvoiceApiArg
      >({
        query: (queryArg) => ({
          url: `/quickpay/2026-05-05/accounts/${queryArg.accountNumber}/invoices/${queryArg.invoiceNumber}`,
        }),
        providesTags: ['quickpay-controller'],
      }),
      getDiscountHistory: build.query<
        GetDiscountHistoryApiResponse,
        GetDiscountHistoryApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2026-02-10/organizations/${queryArg.orgId}/subscriptions/${queryArg.subscriptionNumber}/discounts`,
        }),
        providesTags: ['discount-controller'],
      }),
      getDiscountsByOrganization: build.query<
        GetDiscountsByOrganizationApiResponse,
        GetDiscountsByOrganizationApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2026-02-10/organizations/${queryArg.orgId}/discounts`,
        }),
        providesTags: ['discount-controller'],
      }),
      getOrganizationBillingAccount: build.query<
        GetOrganizationBillingAccountApiResponse,
        GetOrganizationBillingAccountApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2025-06-11/organizations/${queryArg.orgId}`,
        }),
        providesTags: ['billing-controller'],
      }),
      getEligibleAssetIdsGroupedByPricingId: build.query<
        GetEligibleAssetIdsGroupedByPricingIdApiResponse,
        GetEligibleAssetIdsGroupedByPricingIdApiArg
      >({
        query: (queryArg) => ({
          url: `/2026-02-10/organizations/${queryArg.organizationId}/eligible-assets-by-price/${queryArg.productCode}`,
          params: {
            licensingModelId: queryArg.licensingModelId,
            assetType: queryArg.assetType,
            peripheralType: queryArg.peripheralType,
            fleetIds: queryArg.fleetIds,
            assetGroup: queryArg.assetGroup,
          },
        }),
        providesTags: ['asset-license-controller'],
      }),
      getEligibleAssetIds: build.query<
        GetEligibleAssetIdsApiResponse,
        GetEligibleAssetIdsApiArg
      >({
        query: (queryArg) => ({
          url: `/2025-06-11/organizations/${queryArg.organizationId}/eligible-assets-ids/${queryArg.productCode}`,
          params: {
            assetType: queryArg.assetType,
            peripheralType: queryArg.peripheralType,
            fleetIds: queryArg.fleetIds,
            assetGroup: queryArg.assetGroup,
          },
        }),
        providesTags: ['asset-license-controller'],
      }),
      getProductSubscriptionDetails: build.query<
        GetProductSubscriptionDetailsApiResponse,
        GetProductSubscriptionDetailsApiArg
      >({
        query: (queryArg) => ({
          url: `/2025-06-11/organizations/${queryArg.orgId}/subscription-details/${queryArg.productCode}`,
        }),
        providesTags: ['entitlements-controller'],
      }),
      getProductSubscriptionSummary: build.query<
        GetProductSubscriptionSummaryApiResponse,
        GetProductSubscriptionSummaryApiArg
      >({
        query: (queryArg) => ({
          url: `/2025-06-11/organizations/${queryArg.orgId}/eligibility`,
          params: {
            productType: queryArg.productType,
            assetType: queryArg.assetType,
            fleetIds: queryArg.fleetIds,
          },
        }),
        providesTags: ['entitlements-controller'],
      }),
      getSingleProductEligibilitySummary: build.query<
        GetSingleProductEligibilitySummaryApiResponse,
        GetSingleProductEligibilitySummaryApiArg
      >({
        query: (queryArg) => ({
          url: `/2025-06-11/organizations/${queryArg.orgId}/eligibility/${queryArg.productCode}`,
          params: {
            assetType: queryArg.assetType,
            fleetIds: queryArg.fleetIds,
          },
        }),
        providesTags: ['entitlements-controller'],
      }),
      getOrganizationEntitlements: build.query<
        GetOrganizationEntitlementsApiResponse,
        GetOrganizationEntitlementsApiArg
      >({
        query: (queryArg) => ({
          url: `/2024-06-01/organizations/${queryArg.orgId}`,
        }),
        providesTags: ['entitlements-controller'],
      }),
      getLicenseSummaries: build.query<
        GetLicenseSummariesApiResponse,
        GetLicenseSummariesApiArg
      >({
        query: (queryArg) => ({
          url: `/2024-06-01/organizations/${queryArg.orgId}/licenses/summaries`,
        }),
        providesTags: ['license-controller'],
      }),
      deletePaymentMethod: build.mutation<
        DeletePaymentMethodApiResponse,
        DeletePaymentMethodApiArg
      >({
        query: (queryArg) => ({
          url: `/billing/2026-02-24/organizations/${queryArg.orgId}/payment-methods/${queryArg.paymentMethodId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['payment-controller'],
      }),
    }),
    overrideExisting: false,
  });

export { injectedRtkApi as entitlementsApi };
export type UpdateShippingAddressApiResponse =
  /** status 200 Shipping address updated. */ GmResponseCommonShippingAddressDto;
export type UpdateShippingAddressApiArg = {
  /** Organization ID. */
  orgId: string;
  /** Shipping address ID. */
  shippingAddressId: string;
  saveShippingAddressRequestDto: SaveShippingAddressRequestDto;
};
export type CreatePaymentSessionApiResponse =
  /** status 200 OK */ GmResponseCommonPaymentSessionResponseDto;
export type CreatePaymentSessionApiArg = {
  createPaymentSessionRequest: CreatePaymentSessionRequest;
};
export type SubmitQuickpayEmailApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonString;
export type SubmitQuickpayEmailApiArg = {
  submitQuickpayEmailRequest: SubmitQuickpayEmailRequest;
};
export type CreateShippingAddressApiResponse =
  /** status 201 Shipping address created. */ GmResponseCommonShippingAddressDto;
export type CreateShippingAddressApiArg = {
  /** Organization ID. */
  orgId: string;
  saveShippingAddressRequestDto: SaveShippingAddressRequestDto;
};
export type ListOrdersApiResponse =
  /** status 200 Success */
  | GmResponseCommonListPeripheralOrdersResponseDto
  | /** status 204 No results (empty list; response has no body per HTTP semantics) */ GmResponseCommonListPeripheralOrdersResponseDto;
export type ListOrdersApiArg = {
  /** Organization id */
  orgId: string;
  /** Optional filter on shipping details or order number */
  searchBy?: string;
  /** Maximum number of orders to return (1–30) */
  pageSize?: number;
};
export type PlaceOrderApiResponse =
  /** status 201 Order placed in Zuora */ GmResponseCommonPlacePeripheralOrderResponseDto;
export type PlaceOrderApiArg = {
  /** Organization id */
  orgId: string;
  placePeripheralOrderDto: PlacePeripheralOrderDto;
};
export type PreviewPeripheralOrderApiResponse =
  /** status 200 Preview calculated */ GmResponseCommonPreviewPeripheralOrderResponseDto;
export type PreviewPeripheralOrderApiArg = {
  /** Organization id */
  orgId: string;
  previewPeripheralOrderDto: PreviewPeripheralOrderDto;
};
export type RedeemLicenseApiResponse =
  /** status 200 License redeemed */ GmResponseCommonRedeemedLicenseDto;
export type RedeemLicenseApiArg = {
  /** Organization id */
  orgId: string;
  redeemLicenseRequestDto: RedeemLicenseRequestDto;
};
export type ListInvoicesApiResponse =
  /** status 200 Invoices in the range, possibly none */ PartnerInvoiceListResponse;
export type ListInvoicesApiArg = {
  partnerInvoiceListRequest: PartnerInvoiceListRequest;
};
export type SyncOviSubscriptionsApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonSyncOviSubscriptionsResponse;
export type SyncOviSubscriptionsApiArg = {
  syncOviSubscriptionsRequest: SyncOviSubscriptionsRequest;
};
export type ValidateMigratedSubscriptionsApiResponse =
  /** status 200 Validation completed. Check result.valid. */ GmResponseCommonValidateMigratedSubscriptionsResponse;
export type ValidateMigratedSubscriptionsApiArg = {
  syncOviSubscriptionsRequest: SyncOviSubscriptionsRequest;
};
export type UpdateZuoraAccountApiResponse =
  /** status 200 Successfully updated the Zuora account. */ GmResponseCommonUpdateZuoraAccountResponse;
export type UpdateZuoraAccountApiArg = {
  updateZuoraAccountRequest: UpdateZuoraAccountRequest;
};
export type ValidateZuoraAccountApiResponse =
  /** status 200 Validation completed. Check result.valid. */ GmResponseCommonValidateZuoraAccountResponse;
export type ValidateZuoraAccountApiArg = {
  validateZuoraAccountRequest: ValidateZuoraAccountRequest;
};
export type FindMigratedBillingAccountsApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonGmPageMigratedBillingAccount;
export type FindMigratedBillingAccountsApiArg = {
  /** OVI business account ID. */
  accountId?: string;
  /** CSP organization ID. */
  organizationId?: string;
};
export type SyncOviBillingAccountApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonSyncOviBillingAccountResponse;
export type SyncOviBillingAccountApiArg = {
  /** Reapply the snapshot even when its change sequence was already applied. */
  'FORCE-CHANGE'?: 'true' | 'false';
  syncOviBillingAccountRequest: SyncOviBillingAccountRequest;
};
export type ValidateMigratedOviBillingAccountApiResponse =
  /** status 200 Validation completed. */ GmResponseCommonValidateMigratedOviBillingAccountResponse;
export type ValidateMigratedOviBillingAccountApiArg = {
  validateMigratedOviBillingAccountRequest: ValidateMigratedOviBillingAccountRequest;
};
export type UpdateInvoiceMeEnabledIfAllowedApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonBillingAccountDto;
export type UpdateInvoiceMeEnabledIfAllowedApiArg = {
  updateInvoiceMeEnabledRequest: UpdateInvoiceMeEnabledRequest;
};
export type ApplyDiscountToSubscriptionApiResponse =
  /** status 200 Discount applied. */ GmResponseCommonDiscountDto;
export type ApplyDiscountToSubscriptionApiArg = {
  orgId: string;
  subscriptionNumber: string;
  discountRequestDto: DiscountRequestDto;
};
export type ListDiscountsApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonDiscountPageDto;
export type ListDiscountsApiArg = {
  listDiscountsRequest: ListDiscountsRequest;
};
export type ExportDiscountsApiResponse = unknown;
export type ExportDiscountsApiArg = {
  /** Locale from user preferences used to localize CSV headers, enum cell values, and Created At / Cancelled At timestamps (e.g. en-US, fr-CA). Defaults to en-US when omitted. Start Date and End Date are not localized. */
  locale?: string;
  /** IANA time zone identifier used when formatting Created At and Cancelled At, for example America/New_York. Defaults to UTC when omitted or invalid. */
  timeZone?: string;
  discountFilter: DiscountFilter;
};
export type HandleZuoraCalloutApiResponse =
  /** status 200 Webhook processed successfully */ GmResponseCommonMapStringObject;
export type HandleZuoraCalloutApiArg = {
  zuoraMessage: ZuoraMessage;
};
export type HandleInvoicePostedApiResponse =
  /** status 200 OK */ GmResponseCommonBoolean;
export type HandleInvoicePostedApiArg = {
  invoicePostedDto: InvoicePostedDto;
};
export type CreatePaymentSession1ApiResponse =
  /** status 201 Created */ GmResponseCommonPaymentSessionResponseDto;
export type CreatePaymentSession1ApiArg = {
  paymentSessionRequestDto: PaymentSessionRequestDto;
};
export type AddPaymentMethodApiResponse =
  /** status 200 OK */ GmResponseCommonPaymentMethodDto;
export type AddPaymentMethodApiArg = {
  /** Organization ID. */
  orgId: string;
  addPaymentMethodDto: AddPaymentMethodDto;
};
export type GetInvoiceHistoryApiResponse =
  /** status 200 OK */ GmResponseCommonInvoiceHistoryPageDto;
export type GetInvoiceHistoryApiArg = {
  /** The ID of the organization for which the invoices are to be retrieved */
  orgId: string;
  invoiceHistoryRequestDto: InvoiceHistoryRequestDto;
};
export type DownloadInvoicesApiResponse = /** status 200 OK */ Blob;
export type DownloadInvoicesApiArg = {
  /** Organization ID. */
  orgId: string;
  invoiceDownloadRequestDto: InvoiceDownloadRequestDto;
};
export type CreateOrderApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonOrderResponseDto;
export type CreateOrderApiArg = {
  createOrderRequestDto: CreateOrderRequestDto;
};
export type UnenrollAssetsApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonResponseUnenrollAssetsDto;
export type UnenrollAssetsApiArg = {
  requestUnenrollAssetsDto: RequestUnenrollAssetsDto;
};
export type PreviewOrderApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonOrderPreviewResponseDto;
export type PreviewOrderApiArg = {
  orderPreviewRequestDto: OrderPreviewRequestDto;
};
export type EnrollAssetsApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonOrderResponseDto;
export type EnrollAssetsApiArg = {
  requestEnrollAssetsDto: RequestEnrollAssetsDto;
};
export type PreviewEnrollAssetsApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonResponseEnrollAssetsDto;
export type PreviewEnrollAssetsApiArg = {
  previewEnrollAssetsDto: PreviewEnrollAssetsDto;
};
export type ExecuteActionsApiResponse =
  /** status 200 Success */ GmResponseCommonMapStringObject;
export type ExecuteActionsApiArg = {
  commandDto: CommandDto;
};
export type CloneAndOnboardApiResponse =
  /** status 200 Success */ GmResponseCommonMapStringObject;
export type CloneAndOnboardApiArg = {
  cloneAndOnboardDto: CloneAndOnboardDto;
};
export type GetOrganizationAssetsWithPriceApiResponse =
  /** status 200 Success */ GmResponseCommonOrganizationAssetsPageDto;
export type GetOrganizationAssetsWithPriceApiArg = {
  /** The ID of the organization for which to get the assets eligibility summary. */
  organizationId: string;
  assetsWithPricingRequest: AssetsWithPricingRequest;
};
export type GetOrganizationAssetsApiResponse =
  /** status 200 Success */ GmResponseCommonOrganizationAssetsPageDto;
export type GetOrganizationAssetsApiArg = {
  /** The ID of the organization for which to get the assets eligibility summary. */
  organizationId: string;
  listOrganizationAssets: ListOrganizationAssets;
};
export type GetLicensesApiResponse =
  /** status 200 Success */ GmResponseCommonLicensePageDto;
export type GetLicensesApiArg = {
  /** Organization ID. */
  orgId: string;
  getLicenseListDto: GetLicenseListDto;
};
export type CreateLicenseApiResponse =
  /** status 201 Created */ GmResponseCommonLicenseDto;
export type CreateLicenseApiArg = {
  /** Organization ID. */
  orgId: string;
  createLicenseDto: CreateLicenseDto;
};
export type GetOrganizationEnablementsApiResponse =
  /** status 200 OK */ GmResponseCommonEnablementsDto;
export type GetOrganizationEnablementsApiArg = {
  /** Organization ID. */
  orgId: string;
  enablementsFiltersDto: EnablementsFiltersDto;
};
export type ReprocessLicensesForAssetsApiResponse =
  /** status 200 OK */ GmResponseCommonReprocessedLicensesDto;
export type ReprocessLicensesForAssetsApiArg = {
  /** Organization ID. */
  orgId: string;
  reprocessingDto: ReprocessingDto;
};
export type CancelDiscountApiResponse =
  /** status 200 Discount cancelled (status CANCELLED). */ GmResponseCommonDiscountDto;
export type CancelDiscountApiArg = {
  orgId: string;
  discountId: string;
  cancellationReason?: string;
};
export type ChangeDiscountApiResponse =
  /** status 200 Discount changed: the new row for a percent or duration change, or the same row for a reason-only change. */ GmResponseCommonDiscountDto;
export type ChangeDiscountApiArg = {
  orgId: string;
  discountId: string;
  discountRequestDto: DiscountRequestDto;
};
export type SetDefaultPaymentMethodApiResponse =
  /** status 200 Payment method successfully set as default. */ GmResponseCommonPaymentMethodDto;
export type SetDefaultPaymentMethodApiArg = {
  /** The ID of the organization. */
  orgId: string;
  /** The ID of the payment method to set as default. */
  paymentMethodId: string;
};
export type UpdatePaymentMethodApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonPaymentMethodDto;
export type UpdatePaymentMethodApiArg = {
  /** The ID of the organization for which the payment method needs to be updated. */
  orgId: string;
  /** The ID of the payment method within the organization which is to be updated. */
  paymentMethodId: string;
  updatePaymentMethodDto: UpdatePaymentMethodDto;
};
export type UpdateLicenseApiResponse =
  /** status 200 OK */ GmResponseCommonLicenseDto;
export type UpdateLicenseApiArg = {
  /** Organization ID. */
  orgId: string;
  /** License ID. */
  licenseId: string;
  updateLicenseDto: UpdateLicenseDto;
};
export type GetPaymentDetailsApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonQuickpayPaymentResponse;
export type GetPaymentDetailsApiArg = {
  /** The identifying key of the payment. */
  paymentId: string;
};
export type SearchAccountInvoiceApiResponse =
  /** status 200 OK */ GmResponseCommonSearchAccountInvoiceResponse;
export type SearchAccountInvoiceApiArg = {
  /** The account number associated with the invoice. */
  accountNumber: string;
  /** The invoice number to retrieve. */
  invoiceNumber: string;
};
export type GetDiscountHistoryApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonDiscountHistoryDto;
export type GetDiscountHistoryApiArg = {
  orgId: string;
  subscriptionNumber: string;
};
export type GetDiscountsByOrganizationApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonListDiscountDto;
export type GetDiscountsByOrganizationApiArg = {
  /** The ID of the organization. */
  orgId: string;
};
export type GetOrganizationBillingAccountApiResponse =
  /** status 200 OK */ GmResponseCommonBillingAccountDto;
export type GetOrganizationBillingAccountApiArg = {
  /** Organization ID. */
  orgId: string;
};
export type GetEligibleAssetIdsGroupedByPricingIdApiResponse =
  /** status 200 Success */ GmResponseCommonListEligibleAssetsByPricingModelDto;
export type GetEligibleAssetIdsGroupedByPricingIdApiArg = {
  /** The ID of the organization for which to get the assets eligibility summary. */
  organizationId: string;
  /** The code of the product to get eligible asset IDs within the organization. */
  productCode: string;
  /**
                        The code of the specific licensing model, for example the code for the monthly subscription.
                     */
  licensingModelId: string;
  /** Restrict results to a single asset type (vehicle or peripheral). Required when assetGroup is REDEEMABLE. */
  assetType?: 'vehicle' | 'peripheral';
  /** When set, restrict results to one or more peripheral device kinds. Only applies when assetType is peripheral. Pass multiple values by repeating the query parameter, for example `?peripheralType=fleet-camera&peripheralType=adapter`. */
  peripheralType?: ('fleet-camera' | 'fleet-key-module' | 'adapter')[];
  /** When set, restrict results to one or more fleets (max 10). Pass multiple values by repeating the query parameter, for example `?fleetIds=fleet-a&fleetIds=fleet-b`. */
  fleetIds?: string[];
  /** Asset group to return. Defaults to ELIGIBLE. When REDEEMABLE, assetType is required. */
  assetGroup?:
    | 'ELIGIBLE'
    | 'ASSIGNABLE'
    | 'ENROLLED'
    | 'RPO'
    | 'REDEEMABLE'
    | 'TRIAL'
    | 'UNENROLLED'
    | 'ENABLED';
};
export type GetEligibleAssetIdsApiResponse =
  /** status 200 OK */ GmResponseCommonListString;
export type GetEligibleAssetIdsApiArg = {
  /** The ID of the organization for which to get the assets eligibility summary. */
  organizationId: string;
  /** The code of the product to get eligible asset IDs within the organization. */
  productCode: string;
  /** Restrict results to a single asset type (vehicle or peripheral). Required when assetGroup is REDEEMABLE. */
  assetType?: 'vehicle' | 'peripheral';
  /** When set, restrict results to one or more peripheral device kinds. Only applies when assetType is peripheral. Pass multiple values by repeating the query parameter, for example `?peripheralType=fleet-camera&peripheralType=adapter`. */
  peripheralType?: ('fleet-camera' | 'fleet-key-module' | 'adapter')[];
  /** When set, restrict results to one or more fleets (max 10). Pass multiple values by repeating the query parameter, for example `?fleetIds=fleet-a&fleetIds=fleet-b`. */
  fleetIds?: string[];
  /** Asset group to return. Defaults to ELIGIBLE. When REDEEMABLE, assetType is required. */
  assetGroup?:
    | 'ELIGIBLE'
    | 'ASSIGNABLE'
    | 'ENROLLED'
    | 'RPO'
    | 'REDEEMABLE'
    | 'TRIAL'
    | 'UNENROLLED'
    | 'ENABLED';
};
export type GetProductSubscriptionDetailsApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonProductSubscriptionDetailsDto;
export type GetProductSubscriptionDetailsApiArg = {
  /** The ID of the organization for which to get the product subscription details. */
  orgId: string;
  /** Code of the product to get the subscription details. */
  productCode: string;
};
export type GetProductSubscriptionSummaryApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonListProductEligibilitySummaryDto;
export type GetProductSubscriptionSummaryApiArg = {
  /** The ID of the organization for which to get the assets eligibility summary. */
  orgId: string;
  /** When set, return only products whose productType matches (HARDWARE or SERVICE). */
  productType?: 'HARDWARE' | 'SERVICE';
  /** When set, scope each product's eligibility counts to a single asset type (vehicle or peripheral). */
  assetType?: 'vehicle' | 'peripheral';
  /** When set, scope each product's eligibility counts to one or more fleets (max 10). Pass multiple values by repeating the query parameter. */
  fleetIds?: string[];
};
export type GetSingleProductEligibilitySummaryApiResponse =
  /** status 200 Request was successful. */ GmResponseCommonProductEligibilitySummaryDto;
export type GetSingleProductEligibilitySummaryApiArg = {
  /** The ID of the organization for which to get the assets eligibility summary. */
  orgId: string;
  /** Code of the product to get the eligibility summary. */
  productCode: string;
  /** When set, scope the eligibility counts to a single asset type (vehicle or peripheral). */
  assetType?: 'vehicle' | 'peripheral';
  /** When set, scope the eligibility counts to one or more fleets (max 10). Pass multiple values by repeating the query parameter. */
  fleetIds?: string[];
};
export type GetOrganizationEntitlementsApiResponse =
  /** status 200 OK */ GmResponseCommonEntitlementResponseDto;
export type GetOrganizationEntitlementsApiArg = {
  /** Organization ID. */
  orgId: string;
};
export type GetLicenseSummariesApiResponse =
  /** status 200 Success */ GmResponseCommonListLicenseSummaryDto;
export type GetLicenseSummariesApiArg = {
  /** Organization ID. */
  orgId: string;
};
export type DeletePaymentMethodApiResponse =
  /** status 200 Payment method successfully deleted. */ GmResponseCommonPaymentMethodDto;
export type DeletePaymentMethodApiArg = {
  /** The ID of the organization. */
  orgId: string;
  /** The ID of the payment method to delete. */
  paymentMethodId: string;
};
export type ShippingAddressDto = {
  /** Unique identifier for the shipping address. */
  shippingAddressId: string;
  /** Recipient first name */
  firstName: string;
  /** Recipient last name */
  lastName: string;
  /** Company or attention name on the shipping label */
  companyName?: string;
  /** Contact phone number */
  phoneNumber: string;
  /** Address line 1 */
  line1: string;
  /** Address line 2 */
  line2?: string;
  /** City */
  city: string;
  /** State or province */
  state: string;
  /** Postal or ZIP code */
  postalCode: string;
  /** ISO 3166-1 alpha-2 country code */
  countryCode: string;
  /** Whether this is the default shipping address. */
  isDefault: boolean;
};
export type GmResponseError = {
  errorType: 'GENERIC' | 'VALIDATION';
  message: string;
  errorCode: string;
};
export type GmResponseCommonShippingAddressDto = {
  result?: ShippingAddressDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type GmResponseCommon = {
  result?: any;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type SaveShippingAddressRequestDto = {
  /** Recipient first name */
  firstName: string;
  /** Recipient last name */
  lastName: string;
  /** Company or attention name on the shipping label */
  companyName?: string;
  /** Contact phone number */
  phoneNumber: string;
  /** Address line 1 */
  line1: string;
  /** Address line 2 */
  line2?: string;
  /** City */
  city: string;
  /** State or province */
  state: string;
  /** Postal or ZIP code */
  postalCode: string;
  /** ISO 3166-1 alpha-2 country code */
  countryCode: string;
  /** Whether this is the default shipping address. */
  isDefault: boolean;
};
export type PaymentSessionResponseDto = {
  /** Token for the payment session returned from Zuora Create payment method session API. */
  token: string;
};
export type GmResponseCommonPaymentSessionResponseDto = {
  result?: PaymentSessionResponseDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type CreatePaymentSessionRequest = {
  /** Invoice number to create a payment session for. */
  invoiceNumber: string;
  /** Account number associated with the invoice. */
  accountNumber: string;
};
export type GmResponseCommonString = {
  result?: string;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type SubmitQuickpayEmailRequest = {
  /** Email address to send the payment confirmation details. */
  email: string;
  /** The payment invoice number. */
  invoiceNumber: string;
  /** The payment identifier from the external payment service. */
  paymentId: string;
};
export type PeripheralOrderShippingDetailsResponseDto = {
  /** Recipient first name */
  firstName: string;
  /** Recipient last name */
  lastName: string;
  /** Company or attention name on the shipping label */
  companyName?: string;
  /** Contact phone number */
  phoneNumber: string;
  /** Address line 1 */
  line1: string;
  /** Address line 2 */
  line2?: string;
  /** City */
  city: string;
  /** State or province */
  state: string;
  /** Postal or ZIP code */
  postalCode: string;
  /** ISO 3166-1 alpha-2 country code */
  countryCode: string;
};
export type PeripheralOrderLineItemDiscountDto = {
  percent: number;
  reasonCode: string;
  reasonDescription?: string;
};
export type PeripheralOrderLineItemDto = {
  /** Catalog product id */
  productId: string;
  /** Catalog product code */
  code: string;
  /** Quantity ordered */
  quantity: number;
  /** Line total amount */
  totalAmount?: number;
  /** Gross (pre-discount) line subtotal before tax */
  subTotalAmount?: number;
  /** Discount applied on this line as a positive amount; null means no discount applies (treat as 0) */
  discountAmount?: number;
  /** Line tax on the discounted amount */
  tax?: number;
  /** Discount applied on this line */
  discount?: PeripheralOrderLineItemDiscountDto;
};
export type PeripheralOrderListItemDto = {
  /** Order number */
  orderNo: string;
  /** Invoice number for the order when an invoice has been posted, null otherwise */
  invoiceNumber?: string;
  /** When the order was placed */
  datePlaced: string;
  /** Total quantity across lines */
  quantity: number;
  /** Order total amount */
  totalAmount?: number;
  /** Whether the order was prepaid or paid at checkout */
  paymentStatus: 'PREPAID' | 'REGULAR';
  /** Ship-to address and contact details */
  shippingDetails: PeripheralOrderShippingDetailsResponseDto;
  /** Billing and fulfillment handoff status */
  orderStatus:
    | 'PENDING_BILLING'
    | 'BILLED'
    | 'SENT_FOR_FULFILLMENT'
    | 'PLACED_ORDER_WITH_PARTNER'
    | 'PARTIALLY_SHIPPED'
    | 'SHIPPED'
    | 'SHIPMENT_DELIVERED'
    | 'FULFILLMENT_FAILED';
  /** Expected delivery timestamp when known */
  expectedDeliveryAt?: string;
  /** Hardware lines on the order */
  orderItems: PeripheralOrderLineItemDto[];
};
export type ListPeripheralOrdersResponseDto = {
  /** Order rows */
  items: PeripheralOrderListItemDto[];
};
export type GmResponseCommonListPeripheralOrdersResponseDto = {
  result?: ListPeripheralOrdersResponseDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type PeripheralLineQuantitiesDto = {
  /** Total quantity ordered on the line */
  totalCount: number;
  /** Quantity included in billing for the line */
  billingCount: number;
};
export type PeripheralLineItemSummaryDto = {
  licensingModelId: string;
  productId: string;
  productCode: string;
  quantity: number;
};
export type DiscountDuration = {
  type: 'MONTHS' | 'YEARS' | 'PERMANENT';
  value?: number;
};
export type PeripheralDiscountDetailDto = {
  discountPercent: number;
  reasonCode: string;
  reasonDescription?: string;
  duration?: DiscountDuration;
};
export type PeripheralPlanDiscountDetailDto = {
  productCode: string;
  licensingModelId: string;
  discountPercent: number;
  reasonCode: string;
  reasonDescription?: string;
  duration?: DiscountDuration;
};
export type PeripheralLineDiscountsDto = {
  hardwareDiscount?: PeripheralDiscountDetailDto;
  planDiscount?: PeripheralPlanDiscountDetailDto;
};
export type PeripheralHardwareLineSummaryDto = {
  /** Gross (pre-discount) line subtotal before tax and shipping */
  subTotal: number;
  /** Discount applied on this line (positive amount), null when no discount applies */
  discountAmount?: number;
  /** Line tax on the discounted amount, including allocated shipping tax */
  taxAmount: number;
  /** Line shipping cost when allocated */
  shippingCost?: number;
  /** Ordered vs billed quantities for the line */
  quantities: PeripheralLineQuantitiesDto;
  /** Catalog and quantity details for the line */
  item: PeripheralLineItemSummaryDto;
  /** Payment timing for the line */
  paymentType: 'PREPAID' | 'REGULAR';
  /** Hardware and plan discounts applied on the line */
  discounts?: PeripheralLineDiscountsDto;
};
export type PlacePeripheralOrderResponseDto = {
  /** Persisted order id */
  orderId: string;
  /** Billing lifecycle status for the persisted order */
  orderStatus:
    | 'PENDING_BILLING'
    | 'BILLED'
    | 'SENT_FOR_FULFILLMENT'
    | 'PLACED_ORDER_WITH_PARTNER'
    | 'PARTIALLY_SHIPPED'
    | 'SHIPPED'
    | 'SHIPMENT_DELIVERED'
    | 'FULFILLMENT_FAILED';
  /** Gross (pre-discount) hardware subtotal before tax (excludes shipping) */
  subTotal?: number;
  /** Discount applied to the order (positive amount), null when no discount applies (treat as 0) */
  discountAmount?: number;
  /** Total tax on the discounted amount, including hardware and shipping tax */
  taxAmount?: number;
  /** Grand total for the order: subTotal - discountAmount + taxAmount + shippingCost (discountAmount is treated as 0 when null) */
  total?: number;
  /** Order-level shipping cost */
  shippingCost?: number;
  /** Per-line hardware summaries */
  hardwareSummary: PeripheralHardwareLineSummaryDto[];
};
export type GmResponseCommonPlacePeripheralOrderResponseDto = {
  result?: PlacePeripheralOrderResponseDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type DiscountRequestDto = {
  /** Discount percentage to apply. Catalog values may have up to 14 decimal places (e.g. 18.76172607879925). Validation ignores trailing zeros, so scale variants like 18.761726078799250 are accepted. */
  discountPercent: number;
  /** Reason code for the discount */
  reasonCode:
    | 'COMPENSATION'
    | 'ACCOUNT_SAVE_CHURN'
    | 'LEGACY_DISCOUNT'
    | 'CUSTOMER_LOYALTY'
    | 'GOVERNMENT'
    | 'MATCH'
    | 'VOLUME'
    | 'VOLUME_PRICING_10'
    | 'VOLUME_PRICING_30'
    | 'CAP_DISCOUNT'
    | 'OTHER';
  /** Description when reason code is OTHER */
  reasonDescription?: string;
  /** Duration of the discount. Use type PERMANENT for permanent discounts, or MONTHS/YEARS with a value for time-limited discounts */
  duration?: DiscountDuration;
};
export type PlanDiscountRequestDto = {
  /** Catalog product code of the companion service plan */
  productCode: string;
  /** Subscription licensing model id on the companion service product */
  licensingModelId: string;
  /** Discount details applied to the companion plan */
  discount: DiscountRequestDto;
};
export type PeripheralOrderItemDto = {
  /** Id of the licensing model pricing option */
  licensingModelId: string;
  /** Catalog product code */
  productCode: string;
  /** Quantity ordered */
  quantity: number;
  /** Optional percentage discount applied to this hardware line's one-time charge in Zuora. Requires the System Manager role and a percent allowed by the product's licensing model. */
  discount?: DiscountRequestDto;
  /** Optional discount for the companion service plan on this line. Requires the System Manager role and a percent allowed by the plan product's licensing model. Applied to the plan subscription if one already exists, otherwise stored unlinked and applied when the plan subscription is created. */
  planDiscount?: PlanDiscountRequestDto;
};
export type PeripheralOrderShippingDetailsDto = {
  /** Recipient first name */
  firstName: string;
  /** Recipient last name */
  lastName: string;
  /** Company or attention name on the shipping label */
  companyName?: string;
  /** Contact phone number */
  phoneNumber: string;
  /** Address line 1 */
  line1: string;
  /** Address line 2 */
  line2?: string;
  /** City */
  city: string;
  /** State or province */
  state: string;
  /** Postal or ZIP code */
  postalCode: string;
  /** ISO 3166-1 alpha-2 country code */
  countryCode: string;
};
export type PlacePeripheralOrderDto = {
  /** Order line items */
  items: PeripheralOrderItemDto[];
  /** Prepaid vs regular (pay-at-checkout) hardware order */
  orderType: 'PREPAID' | 'REGULAR';
  /** When true, persist ship-to as the organization default shipping address */
  saveShippingAddress: boolean;
  /** Ship-to address for fulfillment. Persisted on the order; not sent to Zuora until ship-to logic is implemented. */
  shippingDetails: PeripheralOrderShippingDetailsDto;
  /** Payment method for REGULAR checkout. When provided, set as the organization default before billing. Pass the Invoice-Me sentinel 'invoice-me-payment-method-id' for a NET_30 / Invoice-Me order (valid only when the billing account is on NET_30 terms); payment is then not collected. When omitted, the organization default payment method on file is used. Omit for PREPAID. */
  paymentMethodId?: string;
};
export type PreviewPeripheralOrderResponseDto = {
  /** Gross (pre-discount) hardware subtotal before tax (excludes shipping) */
  subTotal: number;
  /** Discount applied to the order (positive amount), null when no discount applies (treat as 0) */
  discountAmount?: number;
  /** Total tax on the discounted amount, including hardware and shipping tax */
  taxAmount: number;
  /** Grand total for the order: subTotal - discountAmount + taxAmount + shippingCost (discountAmount is treated as 0 when null) */
  total: number;
  /** Order-level shipping cost */
  shippingCost: number;
  /** Per-line hardware summaries */
  hardwareSummary: PeripheralHardwareLineSummaryDto[];
};
export type GmResponseCommonPreviewPeripheralOrderResponseDto = {
  result?: PreviewPeripheralOrderResponseDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type PreviewPeripheralOrderDto = {
  /** Order line items */
  items: PeripheralOrderItemDto[];
  /** Prepaid vs regular (bill at checkout) hardware order */
  orderType: 'PREPAID' | 'REGULAR';
  /** Ship-to when known at preview. Optional and currently ignored; Zuora preview uses the organization account address on file. */
  shippingDetails?: PeripheralOrderShippingDetailsDto;
};
export type RestrictionConditionDto = {
  type: 'ACTIVE_ASSETS_COUNT' | 'REMOTE_COMMANDS_COUNT';
};
export type UsageRestrictionDto = {
  /** Usage condition that carries expression to be evaluated to check the restriction. */
  condition: RestrictionConditionDto;
  /** Duration of usage restriction. */
  duration?: string;
  /** Max value of a constraint counter allowed by the usage restriction for its duration.  */
  max: number;
};
export type LicensingOptionDto = {
  /** Licensing model type. */
  type:
    | 'SUBSCRIPTION'
    | 'RPO'
    | 'MODEL_YEAR'
    | 'MAKE'
    | 'MODEL'
    | 'TRIAL'
    | 'HARDWARE'
    | 'REDEEMABLE';
  /** Duration of licensing option */
  duration: string;
  /** Usage restrictions condition to utilize the license */
  usageRestrictions?: UsageRestrictionDto[];
  /** Maximum number of seats available for the license */
  maxCapacity?: number;
  /** Allowed enrollment operations */
  allowedEnrollmentOperations?: ('REMOVE' | 'ADD' | 'REPLACE')[];
  /** The id of the licensing model of the licensed product */
  licensingModelId: string;
};
export type AcceptedValuesLicensingOptionDto = LicensingOptionDto & {
  /** Set of accepted values for the attribute for which the license is valid for */
  acceptedValues: string[];
};
export type ModelYearLicensingOptionDto = LicensingOptionDto & {
  /** Start Model year */
  start?: number;
  /** End Model year */
  end?: number;
};
export type SubscriptionLicensingOptionDto = LicensingOptionDto & {
  /** Lower bound of OnStar enrollment date eligibility snapshotted from catalog */
  enrollmentDateStart?: string;
  /** Upper bound of OnStar enrollment date eligibility snapshotted from catalog */
  enrollmentDateEnd?: string;
};
export type TrialLicensingOptionDto = LicensingOptionDto;
export type EnrolledEntitiesDto = {
  /** The ids of the entities that are enrolled in the license. */
  entityIds: string[];
};
export type EnrolledEntitiesByPricingModelDto = {
  /** The ids of the entities that are enrolled in the license under this pricing model. */
  entityIds: string[];
  /** The asset type of the enrolled entities. */
  type: 'VINS' | 'PERIPHERALS';
  /** The pricing model identifier associated with these enrolled entities. */
  pricingId: string;
};
export type ScheduledDto = {
  unenroll?: string[];
};
export type LicenseDto = {
  /** Unique identifier for the license. */
  id: string;
  /** Id of the organization that license belongs to, directly or via asset. */
  organizationId: string;
  /** Short name of the license. */
  name: string;
  /** Channel (flow) through which the license was distributed. */
  channel:
    | 'ONBOARDING'
    | 'ONBOARDING_REPROCESS'
    | 'OFFBOARDING'
    | 'OFFBOARDING_REPROCESS'
    | 'MANUAL'
    | 'CHECKOUT'
    | 'PREPAID_REDEEM';
  /** Unique identifier of the product the license entitles access for. */
  productId: string;
  /** Well known code of the product, does not change across versions of the product. */
  productCode: string;
  /** Licensing option details for the license. It corresponds to the licensing model the license is based on. The attributes available on the option depend on the licensing model. */
  licensingOption:
    | AcceptedValuesLicensingOptionDto
    | ModelYearLicensingOptionDto
    | SubscriptionLicensingOptionDto
    | TrialLicensingOptionDto;
  /** Level at which the license is applicable. */
  level: 'ORGANIZATION' | 'ASSET';
  /** Start date of the license validity period. */
  startDate: string;
  /** License expiration date, for renewing subscription it's null. */
  expirationDate?: string;
  /** Map of asset types to enrolled entities, representing the entities enrolled under the license base pricing model. */
  enrolledEntities?: {
    [key: string]: EnrolledEntitiesDto;
  };
  /** List of entities enrolled under a specific pricing model. */
  enrolledEntitiesByPricingModel?: EnrolledEntitiesByPricingModelDto[];
  /** Description of the license. */
  description?: string;
  /** Current status of the license, */
  status:
    | 'PENDING'
    | 'ACTIVE'
    | 'SUSPENDED'
    | 'TERMINATED'
    | 'EXPIRED'
    | 'REDEEMABLE'
    | 'REDEEMED';
  /** Current payment status of the license. */
  paymentStatus: 'PENDING' | 'COMPLETE' | 'FAILED' | 'NOT_REQUIRED';
  /** List if the identifiers for a dependent licenses, if any. */
  dependentLicenseIds?: string[];
  /** Date and time this license was created. */
  createdDateTime: string;
  /** Unique identifier for the user who created the license. */
  createdBy: string;
  /** Date and time the license was updated. */
  updatedDateTime: string;
  /** Unique identifier for the user who last updated the license. */
  updatedBy: string;
  /** Used computing the asset list on renewal. */
  scheduled?: {
    [key: string]: {
      [key: string]: ScheduledDto;
    };
  };
};
export type RedeemedLicenseDto = {
  /** A license DTO coming from the redemption. */
  license: LicenseDto;
  /** Identifier of the peripheral batch generated to cover for redeemed device activation. Null when no new activation happen (due to idempotency). */
  batchId?: string;
  /** Number of peripheral assets in the batch identified by batchId. For redemption flow it's always 1 when batchId is not null and null if the batchId is null. */
  batchSize?: number;
};
export type GmResponseCommonRedeemedLicenseDto = {
  result?: RedeemedLicenseDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type RedeemLicenseRequestDto = {
  /** Vehicle identification number holding the REDEEMABLE license. */
  vin: string;
  /** Hardware product code from the catalog. */
  hardwareCode: string;
  /** Hardware serial number to redeem. */
  hardwareId: string;
  /**
                Indicates whether the system should block/wait for Azure AI Search index
                synchronization to complete (or for the timeout to expire) before returning
                the response.
             */
  waitOnAiSearchSync?: boolean;
};
export type PartnerInvoice = {
  /** Invoice number. */
  invoiceNumber: string;
  /** Invoice ID. */
  invoiceId: string;
  /** When the invoice was posted (UTC). */
  createdDateTime: string;
  /** Invoice total. */
  amount?: number;
  /** Remaining balance. */
  balance?: number;
};
export type PartnerInvoiceListResponse = {
  /** Tax ID as sent in the request. */
  taxId: string;
  /** Organization the invoices belong to. */
  organizationId: string;
  /** Posted invoices in the range, oldest first. */
  invoices: PartnerInvoice[];
};
export type PartnerInvoiceListRequest = {
  /** Organization whose invoices are requested. */
  organizationId: string;
  /** Tax ID the partner holds for the organization. */
  taxId: string;
  /** First day of the range (MM-DD-YYYY, UTC), inclusive. */
  invoiceFromDate: string;
  /** Last day of the range (MM-DD-YYYY, UTC), inclusive. */
  invoiceToDate: string;
  /** Partner making the request. Defaults to GMF when omitted. */
  providerCode?: 'GMF';
};
export type SyncOviSubscriptionResult = {
  /** OVI product type on the subscription row. */
  productType: string;
  /** OVI billing subscription number for this row. */
  billingSubscriptionNumber: string;
  /** Zuora subscription number created or reused when an order was placed. */
  subscriptionNumber?: string;
  /** CSP organization license ID created when an order was placed. */
  licenseId?: string;
  /** True when an order was placed or reused for this row; false for a no-op. */
  orderPlaced: boolean;
  /** OVI offerId used for the CSP catalog lookup. */
  offerId?: string;
  /** CSP product code mapped from offerId. */
  productCode?: string;
  /** CSP licensing model id mapped from offerId. */
  licensingModelId?: string;
  /** Discount percent mapped from offerId. */
  discountPercent?: number;
  /** Discount reason code sent on createOrder. */
  reasonCode?:
    | 'COMPENSATION'
    | 'ACCOUNT_SAVE_CHURN'
    | 'LEGACY_DISCOUNT'
    | 'CUSTOMER_LOYALTY'
    | 'GOVERNMENT'
    | 'MATCH'
    | 'VOLUME'
    | 'VOLUME_PRICING_10'
    | 'VOLUME_PRICING_30'
    | 'CAP_DISCOUNT'
    | 'OTHER';
  /** Eligible vehicle IDs for this subscription, not yet enrolled. */
  eligibleAssetIds: string[];
};
export type SyncOviSubscriptionsResponse = {
  /** Corresponding CSP organization ID. */
  organizationId: string;
  /** OVI business account ID. */
  oviAccountId: string;
  /** One result per subscription row in the billing snapshot, in snapshot order. */
  subscriptions: SyncOviSubscriptionResult[];
};
export type GmResponseCommonSyncOviSubscriptionsResponse = {
  result?: SyncOviSubscriptionsResponse;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type OviMigrationBillingPaymentMethodSnapshot = {
  type: 'visa' | 'mastercard' | 'amex' | 'invoiceMe';
  billingPaymentMethodId?: string;
  numberTail?: string;
  expirationMonth?: number;
  expirationYear?: number;
  matchesZuoraDefault: boolean;
};
export type OviMigrationZuoraCancel = {
  cancelledDate: string;
  requestedAt?: string;
};
export type OviVehicleSnapshot = {
  vin: string;
  billable: boolean;
  rpoEndsOn?: string;
};
export type OviCameraSnapshot = {
  /** Lytx camera ID. */
  deviceExternalId: string;
  /** Assigned vehicle VIN. Null when the camera is not assigned; it is still billed. */
  vin?: string;
  activationDate: string;
  /** True when the camera's vehicle has no active camera RPO. */
  billable: boolean;
  /** End date of the active camera RPO. Null when none is active. */
  rpoEndsOn?: string;
};
export type OviSubscriptionSnapshot = {
  productType: string;
  billingSubscriptionNumber: string;
  startDate: string;
  chargeThroughDate: string;
  quantity: number;
  customerCancelled: boolean;
  lastBilledOfferId: string;
  /** Next offer from this BCD; null when customerCancelled. */
  offerId?: string;
  /** Rate type on the next offer; null when customerCancelled. */
  rateType?: string;
  discountPercent?: number;
  basePrice?: number;
  totalPrice?: number;
  discountChangeReason?: string;
  discountChangeComment?: string;
  discountChangeDate?: string;
  zuoraCancel?: OviMigrationZuoraCancel;
  vehicles: OviVehicleSnapshot[];
  /** Camera snapshots. Empty except for future lytx.service subscriptions. */
  cameras: OviCameraSnapshot[];
};
export type OviMigrationBilling = {
  oviAccountId: string;
  accountNumber: string;
  /** OVI account type. Allow values are dealer and fleet. */
  accountType: 'fleet' | 'dealer';
  billingAccountNumber: string;
  billCycleDay: number;
  migrationBcdDate: string;
  /** Currency of all prices on this snapshot. Support USD, CAD and MXN. */
  currency: 'USD' | 'CAD' | 'MXN';
  paymentTerm: 'Due Upon Receipt' | 'Net 30';
  state: 'pending' | 'zuoraCancelled' | 'cutoverDone';
  paymentMethod?: OviMigrationBillingPaymentMethodSnapshot;
  subscriptions: OviSubscriptionSnapshot[];
};
export type SyncOviSubscriptionsRequest = {
  /** HVR change operation code for this snapshot (1 = insert, 2 = update). Accepted for consistency with the HVR replication contract but not used by the skeleton endpoint. */
  hvrChangeOperation?: '1' | '2';
  /** Metadata field used to establish a unique and strictly increasing order for every row change across a replication channel. */
  hvrChangeSequence: string;
  /** Time the change was captured in OVI. */
  hvrChangeTime?: string;
  /** Corresponding CSP organization ID. */
  organizationId: string;
  /** OVI business account ID. */
  oviAccountId: string;
  /** Temporary stub of the GFS `getMigrationBilling.json` payload. Required while `bd.migration.ovi.gfs.enabled` is false; ignored when GFS billing is enabled. */
  migrationBilling?: OviMigrationBilling;
};
export type MigrationValidationError = {
  /** Validation error code. */
  code: 'NOT_FOUND' | 'INVALID_STATE' | 'INVALID_FIELD';
  /** Field that failed validation, when applicable. */
  field?: string;
  /** Human-readable validation failure message. */
  message: string;
};
export type ValidateMigratedSubscriptionsResponse = {
  /** True when each non-cancelled migration billing subscription with billable vehicles has a matching organization license. */
  valid: boolean;
  /** OVI business account ID from the request. */
  oviAccountId: string;
  /** CSP organization ID from the request. */
  organizationId: string;
  /** First validation error, or null when [valid] is true. */
  error?: MigrationValidationError;
};
export type GmResponseCommonValidateMigratedSubscriptionsResponse = {
  result?: ValidateMigratedSubscriptionsResponse;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type UpdateZuoraAccountResponse = {
  /** CSP organization ID. */
  cspOrganizationId: string;
  /** OVI business account ID. */
  oviAccountId: string;
  /** Zuora billing account number. */
  billingAccountNumber: string;
  /** Change operation performed against the Zuora account. */
  changeOp: 'created' | 'updated' | 'retired' | 'no-change';
};
export type GmResponseCommonUpdateZuoraAccountResponse = {
  result?: UpdateZuoraAccountResponse;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type UpdateZuoraAccountRequest = {
  /** Corresponding CSP organization ID. */
  cspOrganizationId: string;
  /** OVI business account ID. */
  oviAccountId: string;
  /** Zuora billing account number. */
  billingAccountNumber: string;
};
export type ValidateZuoraAccountResponse = {
  /** True when the CSP billing account and Zuora customer account match expected attributes. */
  valid: boolean;
  /** OVI business account ID from the request. */
  oviAccountId: string;
  /** CSP organization ID from the request. */
  cspOrganizationId: string;
  /** First validation error, or null when [valid] is true. */
  error?: MigrationValidationError;
};
export type GmResponseCommonValidateZuoraAccountResponse = {
  result?: ValidateZuoraAccountResponse;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type ValidateZuoraAccountRequest = {
  /** OVI business account ID. */
  oviAccountId: string;
  /** Corresponding CSP organization ID. */
  cspOrganizationId: string;
  /** Zuora billing account number. */
  billingAccountNumber: string;
};
export type AddressDto = {
  /** First line of the address. */
  addressLine1: string;
  /** Second line of the address (optional). */
  addressLine2?: string;
  /** City name. */
  city: string;
  /** State code. */
  state: string;
  /** Country code. */
  country: string;
  /** Postal code. */
  postalCode: string;
};
export type LastErrorDto = {
  /** Human-readable error message. */
  message: string;
  /** Date when the error occurred. */
  date: string;
};
export type CreditCardPaymentMethodDto = {
  paymentMethodId: string;
  holderName: string;
  status: 'ACTIVE' | 'BLOCKED' | 'CLOSED' | 'SCRUBBED' | 'UNKNOWN';
  address: AddressDto;
  uiStatus: 'BLOCKED' | 'VALID' | 'EXPIRING' | 'EXPIRED' | 'FAILED';
  lastError?: LastErrorDto;
  /** Expiration month for credit/debit cards. */
  expirationMonth: number;
  /** Expiration year for credit/debit cards. */
  expirationYear: number;
  /** Masked credit card number. */
  maskedCreditCardNumber: string;
  /** Type of credit card. */
  creditCardType:
    | 'VISA'
    | 'MASTERCARD'
    | 'AMERICAN_EXPRESS'
    | 'DISCOVER'
    | 'DINERS_CLUB'
    | 'JCB'
    | 'UNKNOWN';
  type: 'CREDITCARD' | 'ACH' | 'INVOICEME';
};
export type AchPaymentMethodDto = {
  paymentMethodId: string;
  holderName: string;
  status: 'ACTIVE' | 'BLOCKED' | 'CLOSED' | 'SCRUBBED' | 'UNKNOWN';
  address: AddressDto;
  uiStatus: 'BLOCKED' | 'VALID' | 'EXPIRING' | 'EXPIRED' | 'FAILED';
  lastError?: LastErrorDto;
  /** Bank name for display purposes. */
  bankName: string;
  /** Masked bank account number. */
  bankAccountMaskedNumber: string;
  /** Type of bank account. */
  bankAccountType:
    | 'CHECKING'
    | 'SAVINGS'
    | 'BUSINESS_CHECKING'
    | 'BUSINESS_SAVINGS';
  type: 'CREDITCARD' | 'ACH' | 'INVOICEME';
};
export type InvoiceMePaymentMethodDto = {
  status: 'ACTIVE' | 'BLOCKED' | 'CLOSED' | 'SCRUBBED' | 'UNKNOWN';
  uiStatus: 'BLOCKED' | 'VALID' | 'EXPIRING' | 'EXPIRED' | 'FAILED';
  lastError?: LastErrorDto;
  paymentMethodId: string;
  type: 'CREDITCARD' | 'ACH' | 'INVOICEME';
};
export type PaymentMethodDto =
  | ({
      type: 'CREDITCARD';
    } & CreditCardPaymentMethodDto)
  | ({
      type: 'ACH';
    } & AchPaymentMethodDto)
  | ({
      type: 'INVOICEME';
    } & InvoiceMePaymentMethodDto);
export type BillingAccountDto = {
  /** Organization ID associated with the billing account. */
  organizationId: string;
  /** Profile ID associated with the billing account. */
  profileId: string;
  /** Account number for the billing account. */
  accountNumber: string;
  /** Account ID for the billing account. */
  accountId: string;
  /** Whether auto-pay is enabled for the account. */
  autoPay: boolean;
  /** Billing batch type for the account. */
  batch?:
    | 'BATCH1'
    | 'BATCH2'
    | 'BATCH3'
    | 'BATCH4'
    | 'BATCH5'
    | 'BATCH6'
    | 'BATCH7'
    | 'BATCH8'
    | 'BATCH9'
    | 'BATCH10'
    | 'BATCH11'
    | 'BATCH12'
    | 'BATCH13'
    | 'BATCH14'
    | 'BATCH15'
    | 'BATCH16'
    | 'BATCH17'
    | 'BATCH18'
    | 'BATCH19'
    | 'BATCH20';
  /** Day of the month when billing cycle occurs. Must be between range of 1-28. Set to 1 if day of month is greater than 28. */
  billCycleDay: number;
  /** Default payment method ID for the account. */
  defaultPaymentMethodId?: string;
  paymentMethods: PaymentMethodDto[];
  /** Language code for the account. */
  languageCode: string;
  /** Currency code for the account. */
  currency: string;
  /** Whether InvoiceMe is enabled for the account. */
  invoiceMeEnabled: boolean;
  /** Payment term applied to invoices for the account. */
  paymentTerm: 'NET_30' | 'DUE_UPON_RECEIPT';
  /** Information about the most recent error related to this billing account. */
  lastError?: LastErrorDto;
  shippingAddresses: ShippingAddressDto[];
};
export type MigratedBillingAccount = BillingAccountDto & {
  /** OVI business account ID mapped to this billing account. */
  oviAccountId: string;
  /** Composite OVI change sequence of the snapshot last applied to this billing account. */
  oviChangeSequence?: string;
};
export type GmPageMigratedBillingAccount = {
  items: MigratedBillingAccount[];
  total_items?: number;
  continuation_token?: string;
};
export type GmResponseCommonGmPageMigratedBillingAccount = {
  result?: GmPageMigratedBillingAccount;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type SyncOviBillingAccountResponse = {
  /** Corresponding CSP organization ID. */
  organizationId: string;
  /** OVI business account ID. */
  accountId: string;
  /** Change applied to the CSP billing account. */
  changeOp: 'created' | 'updated' | 'retired' | 'no-change';
};
export type GmResponseCommonSyncOviBillingAccountResponse = {
  result?: SyncOviBillingAccountResponse;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type OviPaymentMethodSnapshot = {
  /** HVR change operation code for this payment method (1 = insert, 2 = update). Accepted for consistency with the HVR replication contract but not used: the snapshot is always applied as-is. */
  hvrChangeOperation?: '1' | '2';
  /** Monotonic replication sequence for this payment method, used for idempotency. */
  hvrChangeSequence: string;
  /** Time the payment method change was captured in OVI. */
  hvrChangeTime?: string;
  /** Payment instrument type. */
  paymentMethodType: 'invoiceMe' | 'visa' | 'mastercard' | 'amex';
  /** Zuora payment method identifier reused by CSP. Required for credit cards; absent for `invoiceMe`, which has no payment instrument. */
  billingPaymentMethodId?: string;
  /** Name of the card holder. Required for credit cards; absent for `invoiceMe`, which has no payment instrument. */
  holderName?: string;
  /** Payment method status. */
  statusCode: 'active';
  /** Card expiration month, 1-12. Required for credit cards; absent for `invoiceMe`, which has no payment instrument. */
  expirationMonth?: number;
  /** Four digit card expiration year. Required for credit cards; absent for `invoiceMe`, which has no payment instrument. */
  expirationYear?: number;
  /** Last four digits of the card number. Required for credit cards; absent for `invoiceMe`, which has no payment instrument. */
  numberTail?: string;
};
export type SyncOviBillingAccountRequest = {
  /** HVR change operation code for this account (1 = insert, 2 = update). Accepted for consistency with the HVR replication contract but not used: the snapshot is always applied as-is and the resulting change operation is derived from whether a CSP billing account already exists. */
  hvrChangeOperation?: '1' | '2';
  /** Metadata field used to establish a unique and strictly increasing order for every row change across a replication channel. */
  hvrChangeSequence: string;
  /** Time the account change was captured in OVI. */
  hvrChangeTime?: string;
  /** Corresponding CSP organization ID. */
  cspOrganizationId: string;
  /** OVI business account ID. */
  oviAccountId: string;
  /** Must be absent. Only root OVI billing accounts are supported. */
  parentAccountId?: string;
  /** Zuora billing account number. */
  billingAccountNumber: string;
  /** IETF language tag for the account, for example `en-US`. */
  userLanguage: string;
  /** Bill cycle day, 1-28. */
  billCycleDay: number;
  /** OVI account status. */
  statusCode: 'active';
  /** GM business associate code (BAC). */
  businessId?: string;
  /** US employer identification number. */
  ein?: string;
  /** Tax identifier, for example the Mexican RFC. */
  taxId?: string;
  /** Tax regime code, for example the Mexican regimen fiscal. */
  taxCode?: string;
  /** Shipping address lines. Accepted for contract compatibility but not projected onto CSP yet: every sync currently persists an empty shipping address list. Mapping will be added in a follow-up. */
  shippingAddress: string[];
  /** Billing country code. */
  billingCountryCode: 'US' | 'CA' | 'MX';
  /** Payment method snapshot. Absent when the OVI account has no payment method. */
  paymentMethod?: OviPaymentMethodSnapshot;
};
export type ValidateMigratedOviBillingAccountResponse = {
  /** True when the CSP billing account match expected attributes. */
  valid: boolean;
  /** OVI business account ID from the request. */
  oviAccountId: string;
  /** First validation error, or null when [valid] is true. */
  error?: MigrationValidationError;
};
export type GmResponseCommonValidateMigratedOviBillingAccountResponse = {
  result?: ValidateMigratedOviBillingAccountResponse;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type MigratedOviPaymentMethod = {
  /** Payment instrument type. */
  paymentMethodType: 'invoiceMe' | 'visa' | 'mastercard' | 'amex';
  /** Zuora payment method identifier reused by CSP. Required for credit cards; absent for `invoiceMe`, which has no payment instrument. */
  billingPaymentMethodId?: string;
  /** Name of the card holder. Required for credit cards; absent for `invoiceMe`, which has no payment instrument. */
  holderName?: string;
  /** Payment method status. */
  statusCode: 'active';
  /** Card expiration month, 1-12. Required for credit cards; absent for `invoiceMe`, which has no payment instrument. */
  expirationMonth?: number;
  /** Four digit card expiration year. Required for credit cards; absent for `invoiceMe`, which has no payment instrument. */
  expirationYear?: number;
  /** Last four digits of the card number. Required for credit cards; absent for `invoiceMe`, which has no payment instrument. */
  numberTail?: string;
};
export type ValidateMigratedOviBillingAccountRequest = {
  /** Corresponding CSP organization ID. */
  cspOrganizationId: string;
  /** OVI business account ID. */
  oviAccountId: string;
  /** Must be absent. Only root OVI billing accounts are supported, so there is nothing to compare on the CSP billing account. */
  parentAccountId?: string;
  /** Zuora billing account number. */
  billingAccountNumber: string;
  /** IETF language tag for the account, for example `en-US`. */
  userLanguage: string;
  /** Bill cycle day, 1-28. */
  billCycleDay: number;
  /** OVI account status. Accepted for consistency with the sync contract but not compared: the sync flow does not project OVI account status onto the CSP billing account. */
  statusCode: 'active';
  /** GM business associate code (BAC). */
  businessId?: string;
  /** US employer identification number. */
  ein?: string;
  /** Tax identifier, for example the Mexican RFC. */
  taxId?: string;
  /** Expected Zuora customer account name. Has no counterpart on the sync contract and is compared against the reused Zuora customer account. */
  businessName: string;
  /** Tax regime code, for example the Mexican regimen fiscal. */
  taxCode?: string;
  /** Shipping address lines. Accepted for consistency with the sync contract but not compared: the sync flow does not project shipping addresses onto CSP yet. */
  shippingAddress: string[];
  /** Billing country code. */
  billingCountryCode: 'US' | 'CA' | 'MX';
  /** Expected payment method. Absent when the OVI account has no payment method. */
  paymentMethod?: MigratedOviPaymentMethod;
};
export type GmResponseCommonBillingAccountDto = {
  result?: BillingAccountDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type UpdateInvoiceMeEnabledRequest = {
  /** Boolean value indicating if invoice me is enabled or disabled. */
  invoiceMeEnabled: boolean;
  /** The organization identifier whose billing account the request is changing. */
  organizationId: string;
};
export type DiscountCreatedBy = {
  userId: string;
  email: string;
  role: string;
};
export type DiscountDto = {
  /** Discount ID. */
  id: string;
  /** Organization ID the discount belongs to. */
  organizationId: string;
  /** Subscription number the discount applies to. */
  subscriptionNumber?: string;
  /** Licensing model whose term the percent and duration were chosen against, for a discount not yet attached to a subscription. The duration is term-relative, so an edit form offering a duration type must restrict it to this model's term -- MONTHS on a monthly model, YEARS on an annual one -- and 1 YEAR chosen against an annual model grants nothing at all on a monthly one. Null once the discount is attached to a subscription, whose own licensing option is then the term, and on rows created before the field existed (SDPCPLT-7938). */
  licensingModelId?: string;
  /** Discount percentage. */
  discountPercent: number;
  /** Reason code for the discount. */
  reasonCode:
    | 'COMPENSATION'
    | 'ACCOUNT_SAVE_CHURN'
    | 'LEGACY_DISCOUNT'
    | 'CUSTOMER_LOYALTY'
    | 'GOVERNMENT'
    | 'MATCH'
    | 'VOLUME'
    | 'VOLUME_PRICING_10'
    | 'VOLUME_PRICING_30'
    | 'CAP_DISCOUNT'
    | 'OTHER';
  /** Description when reason code is OTHER. */
  reasonDescription?: string;
  /** Discount duration. */
  duration?: DiscountDuration;
  /** Whether the duration is enforced by auto-expiry. */
  durationEnforced: boolean;
  /** What the discount applies to. */
  appliesTo: 'SUBSCRIPTION' | 'VIN_SNAPSHOT';
  /** Who created the discount. */
  createdBy: DiscountCreatedBy;
  /** When the discount was created. */
  createdAt: string;
  /** Current status of the discount. */
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'REPLACED';
  /** Whether the discount is live, as a single value: ACTIVE while it has not ended and can still be edited or cancelled, EXPIRED once nothing more can be done with it. Gate both the badge and the row actions on this alone. See DiscountState for the cases each value covers. Derived at read time; never stored. */
  state: 'ACTIVE' | 'EXPIRED';
  /** First day of the discount period, for rendering the [startDate, endDate] span: the persisted effectiveStartDate, else the BCD-aligned anchorDate of a legacy post-sale row, else the tenant-local creation date. The anchor tier is conditional: it is surfaced only when it precedes the end, otherwise the creation date is. startDate is never after endDate, because a window too short to have an inclusive form reports the single start day rather than an inverted span. Always present. */
  startDate: string;
  /** Last day the discount is in effect, inclusive, or null when it is permanent. Render it as-is: do not subtract a day. */
  endDate?: string;
  /** Product codes associated with the subscription. */
  productCodes?: string[];
  /** Maximum VIN count across products under this subscription. */
  vinCount?: number;
  /** Charge type the discount applies to: SUBSCRIPTION for recurring plan discounts, ONE_TIME for hardware device discounts. Null on legacy/P1 rows that predate the discriminator. */
  chargeType?: 'SUBSCRIPTION' | 'ONE_TIME';
  /** Hardware device count the discount applies to. Populated for hardware (ONE_TIME) discounts; null for subscription discounts and legacy rows. */
  quantity?: number;
};
export type GmResponseCommonDiscountDto = {
  result?: DiscountDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type DiscountPageDto = {
  /** List of discounts in the current page. */
  items: DiscountDto[];
  /** Token provided by the client in the request, echoed back as-is. Null on the first page. */
  token?: string;
  /** Token to request the next page of results. Null when no more pages. */
  nextToken?: string;
  /** Total number of items matching the filter. */
  totalItems: number;
};
export type GmResponseCommonDiscountPageDto = {
  result?: DiscountPageDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type DiscountFilter = {
  /** Filter by specific discount IDs. When provided, only these discounts are returned. */
  ids?: string[];
  /** Filter by organization ID. */
  organizationId?: string;
  /** Filter by discount status(es). */
  status?: ('ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'REPLACED')[];
  /** Filter by reason code(s). */
  reasonCode?: (
    | 'COMPENSATION'
    | 'ACCOUNT_SAVE_CHURN'
    | 'LEGACY_DISCOUNT'
    | 'CUSTOMER_LOYALTY'
    | 'GOVERNMENT'
    | 'MATCH'
    | 'VOLUME'
    | 'VOLUME_PRICING_10'
    | 'VOLUME_PRICING_30'
    | 'CAP_DISCOUNT'
    | 'OTHER'
  )[];
  /** Include discounts created on or after this timestamp. */
  createdAtFrom?: string;
  /** Include discounts created on or before this timestamp. */
  createdAtTo?: string;
  /** Free-text search across subscription number, reason description, and advisor email (stored on Discount.createdBy.email). Case-insensitive substring match. Whitespace-only values are ignored. SDPCPLT-6764. */
  search?: string;
};
export type ListDiscountsRequest = {
  /** Optional filters to narrow results. */
  filter?: DiscountFilter;
  /** Pagination token from a previous response. */
  token?: string;
  /** Number of items per page. */
  itemsPerResponse: number;
};
export type GmResponseCommonMapStringObject = {
  result?: {
    [key: string]: any;
  };
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type ZuoraMessage = {
  eventType:
    | 'INVOICE_POSTED'
    | 'INVOICE_ITEM_CREATED'
    | 'PAYMENT_ACTION'
    | 'PAYMENT_DECLINED'
    | 'PAYMENT_PROCESSED'
    | 'PAYMENT_METHOD_CHANGED'
    | 'PAYMENT_REVERSAL'
    | 'SUBSCRIPTION_CANCELLED';
  entityId: string;
  attributes: {
    [key: string]: any;
  };
};
export type GmResponseCommonBoolean = {
  result?: boolean;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type InvoicePostedDto = {
  /** Account number associated with the invoice. */
  accountNumber: string;
  /** Account ID associated with the invoice. */
  accountId: string;
  /** Source of the invoice. */
  invoiceSource: 'BillRun' | 'API' | 'Unsupported';
  /** Invoice Number of the invoice. */
  invoiceNumber: string;
  /** Invoice ID of the invoice. */
  invoiceId: string;
  /** ID of the invoice source. If an invoice is generated from a bill run,
                |the value is the number of the corresponding bill run. null otherwise. */
  invoiceSourceId?: string;
  /** The CSP organizationId this invoice belongs to. */
  organizationId: string;
  /** The total amount of the invoice. */
  amount: number;
  /** The remaining balance of the invoice. */
  balance: number;
};
export type PaymentSessionRequestDto = {
  /** Organization ID for which the payment session is being created. */
  organizationId: string;
};
export type GmResponseCommonPaymentMethodDto = {
  result?: PaymentMethodDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type AddPaymentMethodDto = {
  /**
                    Indicates if the payment method added should be used for the auto payments.
                    This value gets propagated to the Billing Account.
                    If the attribute is not provided or null there is no change on the autopay attribute of the Billing Account
                 */
  autoPay?: boolean;
  /**
                    Indicates if the payment method added should be set as the default payment method.
                    This value gets propagated to the Billing Account.
                    If the attribute is not provided or null there is no change on the default Payment method of the Billing Account
                 */
  default?: boolean;
  paymentMethodId: string;
};
export type InvoiceEnrollmentDto = {
  /** The enrolled entities in the invoice */
  enrolled: string[];
  /** The type of asset enrolled */
  type: 'VINS' | 'PERIPHERALS';
  /** The product code associated with the enrollment */
  productCode: string;
  /** The amount due on the invoice */
  amount: number;
  /** The amount without tax on the invoice */
  subtotal: number;
  /** The tax amount on the invoice */
  tax: number;
};
export type InvoiceDeviceDto = {
  /** Product code for the hardware line */
  productCode: string;
  /** Peripheral device id; null until fulfillment assigns devices */
  deviceId?: string;
};
export type InvoiceDto = {
  /** The unique ID for the invoice */
  id: string;
  /** The unique number of the invoice */
  invoiceNumber: string;
  /** The amount due on the invoice */
  amount: number;
  /** The balance on the invoice */
  balance: number;
  /** The amount without tax on the invoice */
  amountWithoutTax?: number;
  /** The tax amount on the invoice */
  taxAmount?: number;
  /** A computed attribute. The UI status of each invoice can be derived from the balance field */
  status: 'PAID' | 'UNPAID' | 'OVERDUE';
  /** The due date for this invoice */
  dueDate: string;
  /** The invoice date */
  invoiceDate: string;
  /** The service period start date from invoice items */
  servicePeriodStartDate?: string;
  /** The service period end date from invoice items */
  servicePeriodEndDate?: string;
  /** The enrollments information in this invoice */
  enrollments?: InvoiceEnrollmentDto[];
  /** Hardware devices from invoice items without a licenseId (may coexist with enrollments on mixed invoices) */
  devices?: InvoiceDeviceDto[];
};
export type InvoiceHistoryPageDto = {
  /** List of invoices */
  items: InvoiceDto[];
  /** Continuation token for pagination */
  continuation_token?: string;
  /** Total number of items in the invoice history */
  total_items?: number;
};
export type GmResponseCommonInvoiceHistoryPageDto = {
  result?: InvoiceHistoryPageDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type InvoiceHistoryRequestDto = {
  /** Optional. Fields to sort by. Default to invoiceDate.DESC */
  sort?: string[];
  /** Optional. The filtering condition */
  filter?: string[];
  /**
                Optional. The forward-only token used to retrieve
                the next set of result mapped to cursor of queryInvoicesApi
                 */
  token?: string;
  /**
                Optional. The number of items to return in the response.
                Default is 10, maximum is 99.
                If not specified, the default value will be used.
             */
  itemsPerResponse?: number;
};
export type InvoiceDownloadRequestDto = {
  /**
                The list of invoice keys to download. Minimum 1, maximum 10.
                Each key must be alphanumeric characters and hyphens only, up to 64 characters long.
                 */
  invoiceKeys: string[];
};
export type TrialVehicleSummaryDto = {
  /** Number of trial vehicles */
  count: number;
  /** Earliest trial end date among trial vehicles */
  earliestTrialEndDate: string;
  /** Latest trial end date among trial vehicles */
  latestTrialEndDate: string;
};
export type QuantitySummaryDto = {
  /** Total quantity associated with this scope */
  totalCount: number;
  /** Billable quantity associated with this scope */
  billingCount: number;
  /** Quantity of re-enrolled assets in this scope */
  reenrollmentCount?: number;
  /** Summary of trial vehicles in this scope */
  trialVehicles?: TrialVehicleSummaryDto;
};
export type ChargeItemSummaryDto = {
  /**
                Id of the pricing model associated with this invoice charge item.
                This will be null if the charge item is not associated with any pricing model.
             */
  pricingId?: string;
  /** The quantity for this charge item */
  quantity: number;
  /** The unit price for this charge item */
  unitPrice: number;
  /** The pre-discount subtotal (unit price x quantity), not including tax for the charge item */
  subTotal: number;
  /** The tax amount on the charge (reflects tax on the discounted amount when discount applied) */
  taxAmount: number;
  /** The total amount on the charge (post-discount amount paid) */
  total: number;
  /** Quantity summary for this charge item */
  quantities: QuantitySummaryDto;
  /** Positive magnitude (absolute value) of the net matching discount-line subtotals for the charge item, or null when no discount line matches or the matching lines net to exactly zero. Mixed-sign lines (for example a discount on a new charge plus a reverse-discount on a proration credit) are netted before the absolute value is applied, so the value is never serialized as 0.00, and a non-null value is not by itself proof of a net price reduction; use discountedUnitPrice to determine whether the per-unit price is actually reduced. */
  discountAmount?: number;
  /** Net unit price after discount, or null when no net discount reduces this charge item. Derived server-side as (subTotal - discountAmount) / quantity from the Zuora-billed amounts, rounded to two decimals with RoundingMode.DOWN to match the posted invoice. Read this value rather than recomputing from unitPrice and a percentage; a client-side recompute can differ by a cent. Null is returned even when discountAmount is non-null if the matching discount lines net to a non-negative subtotal (for example a reverse-discount that outweighs the discount) or if quantity is not positive, so consumers must treat discountedUnitPrice == null as 'no net per-unit discount' rather than assuming it is always set whenever discountAmount is present. */
  discountedUnitPrice?: number;
};
export type InvoiceItemSummaryDto = {
  /** The pre-discount subtotal (unit price x quantity), not including tax */
  subTotal: number;
  /** The tax amount on the order (reflects tax on the discounted amount when discount applied) */
  taxAmount: number;
  /** The total amount on the order (post-discount amount paid) */
  total: number;
  /** Quantity summary for this invoice item, including trial and reenrollment breakdowns */
  quantities: QuantitySummaryDto;
  /**
                The starting date for the plan, which accounts for the order date provided, if any.
                Adjusts to existing Bill Cycle Date, if any.
             */
  servicePeriodStartDate: string;
  /** The ending date for the plan */
  servicePeriodEndDate: string;
  /** Flag indicating if the invoice is for a prorated term, false indicates a full term invoice */
  isProrated: boolean;
  /** Discount percentage for this invoice line. The Zuora invoice preview carries no per-period percentage, so plan lines are mapped null and then annotated: the order preview, place-order, enroll-preview and enroll-assets flows overlay the requested or already-persisted discount onto every line that carries a discountAmount. Peripheral hardware order responses set it directly from the per-item discount on the order request. It stays null on any line carrying no discountAmount. Subscription details is annotated per line rather than per response: its next-term line comes from the order preview and is annotated, while its current-period line is built from the last posted invoice and never is, so that line reports null here even when the period was discounted. A null is therefore not evidence that the period is undiscounted -- read the top-level discount object for the plan's discount terms. */
  discountPercent?: number;
  /** Reason code for the discount on this invoice line, serialized as the DiscountReasonCode name. Populated under exactly the same conditions as discountPercent, from the same discount, so the two are always either both set or both null on a given line. */
  discountReasonCode?: string;
  /** Positive magnitude of the discount applied to this service period, or null when the period carries no discount line. Discount lines in the period are netted before the absolute value is taken, so this is never serialized as 0.00. On a line mapped from a Zuora invoice this is the per-period signal to branch on, because it comes from the invoice itself, whereas discountPercent and discountReasonCode are annotations the plan flows add on top of it and are absent on lines that skip that step. The subscription-details current-period line is the exception: it is built from the recorded billing action for the last posted invoice, which carries no discount field, so it reports null here even when the period was discounted -- read the top-level discount object for that line. subTotal stays the gross pre-discount amount, so on a line that carries a discountAmount the pre-tax amount owed for the period is subTotal - discountAmount, while total is that same amount net of discount and including tax. */
  discountAmount?: number;
  /** Charge items included in this invoice */
  chargeItems: ChargeItemSummaryDto[];
  /** Product code for this invoice line. Used for peripheral hardware orders with multiple items. */
  productCode?: string;
  /**
                Last payment date recorded for this invoice item, if available.
                 Null means no payment has been recorded for the subscription.
             */
  lastPaymentDate?: string;
};
export type OrderResponseDto = {
  /** List containing invoice details */
  invoiceItemSummary: InvoiceItemSummaryDto[];
  /** Created order number */
  orderNumber: string;
  /** The status of the order */
  status:
    | 'PENDING'
    | 'COMPLETE'
    | 'SCHEDULED'
    | 'EXECUTING'
    | 'FAILED'
    | 'CANCELED'
    | 'UNSUPPORTED';
  /** Created subscription numbers */
  subscriptionNumbers: string[];
  /** Identifier of the peripheral batch generated when peripheral (non-VIN) assets are enrolled in this order. Null when the order contains no peripheral assets. */
  batchId?: string;
  /** Number of peripheral (non-VIN) assets in the batch identified by batchId. Null when the order contains no peripheral assets. */
  batchSize?: number;
};
export type GmResponseCommonOrderResponseDto = {
  result?: OrderResponseDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type OviLicenseProperties = {
  /** OVI next-offer id (GFS subscription.offerId) used for migration order placement. */
  offerId: string;
  /** OVI Zuora subscription number on the reused billing account; used for migration order idempotency within the organization. */
  billingSubscriptionNumber: string;
};
export type CreateOrderRequestDto = {
  /** ID for the organization previewing the order */
  organizationId: string;
  /** The CSP productCode getting ordered */
  productCode: string;
  /** Index of the licensing model that represents particular pricing option for the product */
  licensingModelIndex?: number;
  /**
                Id of the licensing model that represents particular pricing option for the product. Either
                licensingModelId or licensingModelIndex must be provided (prefer licensingModelId).
             */
  licensingModelId: string;
  /** Entities to enroll under the license created with the order by asset type */
  enrolledEntities: {
    [key: string]: string[];
  };
  /** Optional discount to apply to the order */
  discount?: DiscountRequestDto;
  /** OVI migration subscription lineage; persisted on the organization license when place-order runs with SystemAction.OVI_MIGRATION. */
  oviProperties?: OviLicenseProperties;
  /**
                Indicates whether the system should block/wait for Azure AI Search index
                synchronization to complete (or for the timeout to expire) before returning
                the response.
             */
  waitOnAiSearchSync?: boolean;
};
export type ResponseUnenrollAssetsDto = {
  /**
                The list of the entities scheduled for the unenrollment.
                This is a subset of the entities from the request, filtered to only those that are
                currently enrolled under the license.

                If the original list is fully valid, this will exactly match the request.
                If none of the entities are enrolled, this list will be empty and the operation will still succeed.
             */
  unenrolling: {
    [key: string]: string[];
  };
  /**
                Date when the entities listed in `unenrolling` will be removed from the license.
                Until this date, they will remain enrolled and entitled to the product.
             */
  unenrollingOn: string;
};
export type GmResponseCommonResponseUnenrollAssetsDto = {
  result?: ResponseUnenrollAssetsDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type RequestUnenrollAssetsDto = {
  /** ID for the organization previewing the order. */
  organizationId: string;
  /** The CSP productCode from which entities will be unenrolled. */
  productCode: string;
  /**
                Optional. The entities to be unenrolled from the license on the Bill Cycle Date (BCD).
                If not provided, all currently enrolled entities will be scheduled for unenrollment,
                and the license will be marked as EXPIRED at BCD.
             */
  entitiesToUnenroll?: {
    [key: string]: string[];
  };
  /** Optional. A reason for the unenrollment. */
  unenrollReason?: string;
  /**
                Flag to indicate immediate removal (offboarding flow).
                If true: Assets removed immediately from license, Zuora updated for next BCD only.
                If false (default): Scheduled unenrollment - assets stay until BCD, then removed via webhook.
             */
  immediateRemoval?: boolean;
  /**
                Indicates whether the system should block/wait for Azure AI Search index
                synchronization to complete (or for the timeout to expire) before returning
                the response.
             */
  waitOnAiSearchSync?: boolean;
};
export type DiscountSummaryDto = {
  /** Id of the persisted discount, matching DiscountDTO.id from the discount management endpoints, so a client can drive the id-scoped change/cancel routes off this summary without a separate org-wide list lookup. Null for a preview describing a not-yet-persisted requested discount (order preview, add-VIN enroll preview), where no discount row exists yet. */
  id?: string;
  /** Zuora subscription number the discount is applied to. */
  subscriptionNumber?: string;
  /** Licensing model whose term the percent and duration were chosen against, for a discount not yet attached to a subscription. The duration is term-relative, so a client offering a term choice at activation must restrict it to this model: 1 YEAR chosen against an annual model grants nothing at all on a monthly one. Null once the discount is attached to a subscription, whose own licensing option is then the term, and on rows created before the field existed (SDPCPLT-7938). */
  licensingModelId?: string;
  /** Discount percentage applied. */
  percent: number;
  /** Reason code for the discount. */
  reasonCode:
    | 'COMPENSATION'
    | 'ACCOUNT_SAVE_CHURN'
    | 'LEGACY_DISCOUNT'
    | 'CUSTOMER_LOYALTY'
    | 'GOVERNMENT'
    | 'MATCH'
    | 'VOLUME'
    | 'VOLUME_PRICING_10'
    | 'VOLUME_PRICING_30'
    | 'CAP_DISCOUNT'
    | 'OTHER';
  /** Description when reason code is OTHER. */
  reasonDescription?: string;
  /** Discount duration. Type PERMANENT has no value; MONTHS/YEARS require a positive value. */
  duration?: DiscountDuration;
  /** Current status of the discount. */
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'REPLACED';
  /** Whether the discount is live, as a single value: ACTIVE while it has not ended and can still be edited or cancelled, EXPIRED once nothing more can be done with it. Same field and same resolution as DiscountDTO.state, so a badge renders identically here and on the discounts table. Derived at read time; never stored. ACTIVE on a preview describing a not-yet-persisted requested discount. */
  state: 'ACTIVE' | 'EXPIRED';
  /** First day of the discount period, for rendering the [startDate, endDate] span: the persisted effectiveStartDate, else the BCD-aligned anchorDate of a legacy post-sale row, else the tenant-local creation date. The anchor tier is conditional: it is surfaced only when it precedes the end, otherwise the creation date is. startDate is never after endDate on a persisted read. On a preview describing a not-yet-persisted requested discount it is an invoice-line service-period start instead: the earliest discounted line on the add-VIN enroll preview, and the earliest line of any kind on the new-order preview. */
  startDate?: string;
  /** Last day the discount is in effect, inclusive, or null when it is permanent. Render it as-is: do not subtract a day. */
  endDate?: string;
};
export type OrderPreviewResponseDto = {
  /**
              List containing invoice details. It should contain one for the current period and one for the next period.
                These are guaranteed to be ordered by the service period start date ascending, so the first one is for
                the current period and the second one is for the next period.
             */
  invoiceItemSummary: InvoiceItemSummaryDto[];
  /** Discount applied to this order, if any */
  discount?: DiscountSummaryDto;
  /** Describes the trial composition of the requested VINs. Trial VINs are excluded from the price preview (they convert to paid automatically at trial expiry). The invoiceItemSummary reflects charges for non-trial VINs only. */
  vinTypes: 'NON_TRIAL_ONLY' | 'TRIAL_ONLY' | 'TRIAL_AND_NON_TRIAL';
};
export type GmResponseCommonOrderPreviewResponseDto = {
  result?: OrderPreviewResponseDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type OrderPreviewRequestDto = {
  /** ID for the organization previewing the order */
  organizationId: string;
  /** The CSP productCode getting ordered */
  productCode: string;
  /** Index of the licensing model that represents particular pricing option for the product */
  licensingModelIndex?: number;
  /**
                Id of the licensing model that represents particular pricing option for the product. Either
                licensingModelId or licensingModelIndex must be provided (prefer licensingModelId).
             */
  licensingModelId: string;
  /** Entities to enroll under the license created with the order by asset type */
  enrolledEntities: {
    [key: string]: string[];
  };
  /** Optional discount to apply to the order preview */
  discount?: DiscountRequestDto;
};
export type RequestEnrollAssetsDto = {
  /** ID for the organization previewing the order. */
  organizationId: string;
  /** The CSP productCode to enroll entities. */
  productCode: string;
  /**
                The entities to be enrolled under the license in addition to the currently enrolled entities.
                The map keys represent entity types (e.g., "VINS", "PERIPHERALS"), and values are lists of corresponding identifiers.
             */
  enrolledEntities: {
    [key: string]: string[];
  };
  /** Optional discount to apply to the subscription this enrollment updates. Requires the SYSTEM_MANAGER role. Ignored when the subscription already has a currently-effective discount, and on non attribute-based-pricing licenses. Applied before the enrollment order, so the newly enrolled VINs' proration is invoiced at the discounted price. Best-effort: if Zuora rejects the apply, the enrollment proceeds at list price; if Zuora accepts the apply but the record fails to save, this order is still priced with the discount; and a discount that applied stays on the subscription if the enrollment order then fails. */
  discount?: DiscountRequestDto;
  /**
                Indicates whether the system should block/wait for Azure AI Search index
                synchronization to complete (or for the timeout to expire) before returning
                the response.
             */
  waitOnAiSearchSync?: boolean;
};
export type ResponseEnrollAssetsDto = {
  /**
                List containing invoice details. Since the subscriptions are renewable,
                the list will always contain 2 elements – one is for the amount charged today
                (could be prorated to the BCD for the quantity change), and the full amount charged
                the next cycle (for extra entities only), both elements contain start/end dates
                for the respective billing cycles.
             */
  invoiceItemSummary: InvoiceItemSummaryDto[];
  /** Describes the trial composition of the requested VINs. Trial VINs are excluded from the billing preview (they convert to paid automatically at trial expiry). The invoiceItemSummary reflects charges for non-trial VINs only. */
  vinTypes: 'NON_TRIAL_ONLY' | 'TRIAL_ONLY' | 'TRIAL_AND_NON_TRIAL';
  /** Discount reflected in this preview: the subscription's existing discount, or the discount the request supplied when the subscription has none (System Manager add-VIN checkout). Either way invoiceItemSummary already reflects the discounted amounts. */
  discount?: DiscountSummaryDto;
};
export type GmResponseCommonResponseEnrollAssetsDto = {
  result?: ResponseEnrollAssetsDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type PreviewEnrollAssetsDto = {
  /** ID for the organization previewing additional entities enrollment. */
  organizationId: string;
  /** The CSP productCode to have additional entities enrolled to. */
  productCode: string;
  /** Map of asset types to their entity IDs to be enrolled. Used to calculate accurate billing preview accounting for re-enrollment scenarios. */
  enrolledEntities: {
    [key: string]: string[];
  };
  /** Optional discount to preview against the add-VIN order. Applied only when the existing subscription has no active discount; when an active discount already exists it wins and this field is ignored. Requires the SYSTEM_MANAGER role, matching the order-preview discount path. Preview only: no Zuora amendment is submitted and no Discount is persisted. */
  discount?: DiscountRequestDto;
};
export type CommandDto = {
  actions: string[];
  input: {
    [key: string]: any;
  };
};
export type CloneAndOnboardDto = {
  orgId: string;
  fleetId?: string;
  vin: string;
  onStarDate?: string;
  deliveryDate?: string;
  modelYear?: number;
  rpos?: string[];
};
export type LicenseChangeRequest = {
  targetLicensingModelId: string;
  targetProductCode: string;
  requestedAt: string;
};
export type PriceDto = {
  /** The price of the product for the specific licensing model. */
  price: number;
  /** The currency of the price. */
  currency?: string;
  /**
                The id of the licensing model that this price is for For example, this price might be for the
                monthly licensing model of the product.
             */
  licensingModelId: string;
  /**
                The id of the pricing model that this price is for For example, this price might be for the
                monthly licensing model of the product for vehicles with a model year later than 2025.
             */
  pricingModelId: string;
  /**
                In the case of attribute based pricing, the code of the pricing model condition that matched the vehicle.
                This is used by the UI to label the condition that matched the vehicle to this price.
                This may be null if there are no attribute conditions in the pricing model, which is expected when the
                licensing model has only one pricing model.
             */
  conditionCode?: string;
};
export type VehicleAssetDto = {
  assetType: 'vehicle';
} & OrganizationAssetDtoBase & {
    /** Product licenses for this asset */
    productLicenses: any[];
    pricingInformation?: PriceDto;
    /** Vehicle identification number */
    vin?: string;
    /** Vehicle make */
    vehicleMake?: string;
    /** Vehicle model */
    vehicleModel?: string;
    /** Vehicle year */
    vehicleYear?: string;
    /** License plate number */
    licensePlate?: string;
    /** Vehicle RPO codes */
    vehicleRpoCodes: string[];
    /** Vehicle PEG code */
    peg?: string;
    /** Vehicle merchandising model */
    merchandisingModel?: string;
    /** Vehicle capabilities as key-value pairs */
    vehicleCapabilities: {
      [key: string]: string;
    };
    /** Vehicle's current OnStar connection status (e.g. CONNECTED, DISCONNECTED). Sourced from the AI Search index and may be null if unknown. */
    onStarConnectionStatus?: string;
  };
export type ProductLicenseDto = {
  /** Product code */
  productCode: string;
  /** License type */
  type?: 'SUBSCRIPTION' | 'TRIAL' | 'PRE_PAID' | 'REDEEMED';
  /** License sub-type */
  subType?:
    | 'COMP'
    | 'FACTORY'
    | 'MODEL_YEAR'
    | 'MODEL'
    | 'MAKE'
    | 'RPO'
    | 'REDEEMED'
    | 'REDEEMABLE';
  /** License start date */
  startDate?: string;
  /** License end date */
  endDate?: string;
  /** License level: ASSET or ORGANIZATION */
  level: 'ORGANIZATION' | 'ASSET';
  /** License change request details, if any */
  licenseChangeRequest?: LicenseChangeRequest;
  /** Term duration for the license, applicable both for termed and renewable licenses. */
  duration: string;
  /** Linked assets this license was redeemed to (device) or licensed under (vehicle). Asset attributes only. null when the license carries no link (not a redeemable/redeemed HW license); empty [] when the license has a link but no linked asset is resolved — either an RPO license that is REDEEMABLE but not yet redeemed, or the referenced asset is absent from the index. */
  link?: (PeripheralAssetDto | VehicleAssetDto)[];
};
export type OrganizationAssetDtoBase = {
  /** Asset identifier */
  id: string;
  /** Asset type discriminator */
  assetType: 'vehicle' | 'peripheral';
  /** List of feature codes for this asset */
  features: string[];
  /** Product licenses for this asset */
  productLicenses: ProductLicenseDto[];
  /** Fleet identifier */
  fleetId?: string;
  /** Fleet name */
  fleetName?: string;
  /** Asset name */
  assetName?: string;
  /**
            This supports providing a price along with the asset information.
            The price is provided for the asset according to the selected product for a specific licensing model and
            according to a condition type in attribute based pricing.
         */
  pricingInformation?: PriceDto;
};
export type PeripheralAssetDto = {
  assetType: 'peripheral';
} & OrganizationAssetDtoBase & {
    /** Product licenses for this asset */
    productLicenses: any[];
    pricingInformation?: PriceDto;
    /** Manufacturer/brand (e.g. LYTX, GEOTAB) */
    make?: string;
    /** Model designation (e.g. AI-14, GO9) */
    model?: string;
    /** Peripheral type (e.g. fleet-camera, adapter) */
    peripheralType?: string;
    /** Hardware revision */
    version?: string;
    /** Device serial number */
    serialNumber?: string;
    /** Device IMEI */
    imei?: string;
    /** Software/firmware version on the device */
    firmware?: string;
    /** Whether the peripheral was created from a pre-paid order */
    isPrepaid?: boolean;
  };
export type OrganizationAssetsPageDto = {
  /** List of organization assets */
  assets: (PeripheralAssetDto | VehicleAssetDto)[];
  /** Total number of assets matching the request */
  totalItems: number;
  /** Token to request the current page of results */
  token?: string;
  /** Token to request the next page of results */
  nextToken?: string;
};
export type GmResponseCommonOrganizationAssetsPageDto = {
  result?: OrganizationAssetsPageDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type AssetsWithPricingRequestFilters = {
  /** Filter by pricing model id. */
  pricingModelIdsFilter?: string[];
  /** Filter by fleet id (max 10). */
  fleetIds?: string[];
};
export type AssetsWithPricingRequest = {
  /** Assets that are enrolled or eligible for this product code. */
  productCode: string;
  /** Assets that are enrolled or eligible for this licensing model Id (annual or monthly). */
  licensingModelId: string;
  /** Used to return assets that are ELIGIBLE, ASSIGNABLE, ENROLLED, UNENROLLED, PREPAID, REDEEMABLE, or TRIAL. When REDEEMABLE, assetType is required. */
  type:
    | 'ELIGIBLE'
    | 'ASSIGNABLE'
    | 'ENROLLED'
    | 'RPO'
    | 'REDEEMABLE'
    | 'TRIAL'
    | 'UNENROLLED'
    | 'ENABLED';
  /** Optional search term applied to all asset attributes. */
  search?: string;
  /** Optional filter to restrict results to a single asset type (vehicle or peripheral). Required when type includes REDEEMABLE. */
  assetType?: 'vehicle' | 'peripheral';
  /** Optional filter to restrict peripheral assets to specific device kinds (fleet-camera, fleet-key-module, adapter). Only applies to peripheral assets. */
  peripheralType?: ('fleet-camera' | 'fleet-key-module' | 'adapter')[];
  /** Optional filter for narrowing down the assets displayed. */
  filters?: AssetsWithPricingRequestFilters;
  /** Pagination token for the page being requested, obtained from the previous response. */
  token?: string;
  /** Number of items to return per page (default: 10, max: 99). */
  itemsPerResponse?: number;
};
export type ListOrganizationAssets = {
  /** List of product codes to filter assets by */
  productCodes: string[];
  /** Asset groups to return: ELIGIBLE, ASSIGNABLE, ENROLLED, UNENROLLED, PREPAID, REDEEMABLE, or TRIAL. When REDEEMABLE is included, assetType is required. */
  type: (
    | 'ELIGIBLE'
    | 'ASSIGNABLE'
    | 'ENROLLED'
    | 'RPO'
    | 'REDEEMABLE'
    | 'TRIAL'
    | 'UNENROLLED'
    | 'ENABLED'
  )[];
  /** Optional search term applied to all asset attributes */
  search?: string;
  /** Optional filter to restrict results to a single asset type (vehicle or peripheral). Required when type includes REDEEMABLE. */
  assetType?: 'vehicle' | 'peripheral';
  /** Optional filter to restrict peripheral assets to specific device kinds (fleet-camera, fleet-key-module, adapter). Only applies to peripheral assets. */
  peripheralType?: ('fleet-camera' | 'fleet-key-module' | 'adapter')[];
  /** Optional filter to restrict results to one or more fleets (max 10). */
  fleetIds?: string[];
  /** Pagination token for the page being requested, obtained from the previous response */
  token?: string;
  /** Number of items to return per page (default: 10, max: 99) */
  itemsPerResponse?: number;
};
export type LicensePageDto = {
  items: LicenseDto[];
  totalItems?: number;
  previousToken?: string;
  token?: string;
  nextToken?: string;
  firstPageItemIndex: number;
};
export type GmResponseCommonLicensePageDto = {
  result?: LicensePageDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type FilterBy = any;
export type ArrayContainsFilter = FilterBy & {
  allOfValues: string[];
};
export type Between = {
  min: string;
  max: string;
};
export type BetweenFilter = FilterBy & {
  between: Between;
};
export type InListFilter = FilterBy & {
  oneOfValues: string[];
};
export type GetLicenseListDto = {
  sort?: {
    [key: string]: 'ASC' | 'DESC';
  };
  filter?: {
    [key: string]: ArrayContainsFilter | BetweenFilter | InListFilter;
  };
  token?: string;
  itemsPerResponse: number;
};
export type GmResponseCommonLicenseDto = {
  result?: LicenseDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type CreateLicenseDto = {
  /** Product ID to create a license for */
  productId: string;
  /** The id of the licensing model from the product catalog. */
  licensingModelId: string;
  /** Start date of the license to be created */
  startDate: string;
  /** Expiration date of the license to be created */
  expirationDate?: string;
  /** The initial payment status of the license created. */
  paymentStatus: 'PENDING' | 'NOT_REQUIRED';
  /** Enrolled entities by asset type */
  enrolledEntities?: {
    [key: string]: EnrolledEntitiesDto;
  };
  /** Description of the license to be created */
  description?: string;
};
export type ProductEnablementDto = {
  /** Product code */
  code: string;
  /** License type (e.g., TRIAL, SUBSCRIPTION, PRE_PAID) */
  licenseType?: 'SUBSCRIPTION' | 'TRIAL' | 'PRE_PAID' | 'REDEEMED';
};
export type EnablementsDto = {
  /** Superset os the all features codes by assert Type, by assetId the asset is both entitled (directly or via organization) and capable of. */
  features: {
    [key: string]: {
      [key: string]: string[];
    };
  };
  /** List of the products codes by assert Type, by assetId the asset is both entitled (directly orvia organization) and capable of. */
  products: {
    [key: string]: {
      [key: string]: string[];
    };
  };
  /** Detailed product enablements by asset type, by assetId. Each product includes the code and license type. */
  productDetails: {
    [key: string]: {
      [key: string]: ProductEnablementDto[];
    };
  };
  /** Last time the returned entities were updated (either license they have or their capabilities) */
  updatedAt: string;
};
export type GmResponseCommonEnablementsDto = {
  result?: EnablementsDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type EnablementsFiltersDto = {
  /** Enablements updated after this time are included */
  updatedSince: string;
  /** Optional asset type scope. Used only when assetIds is null or empty; ignored by the service when assetIds is non-empty. */
  assetType?: 'VINS' | 'PERIPHERALS';
  /** Optional asset ids (globally unique across asset types). When non-empty, assetType is ignored by the service. */
  assetIds?: string[];
};
export type ProductLink = {
  type: 'LICENSED_TO' | 'REDEEMED_TO';
  assetId: string;
};
export type ProductInfo = {
  productCode: string;
  licenseLevel: 'ORGANIZATION' | 'ASSET';
  type: 'SUBSCRIPTION' | 'TRIAL' | 'PRE_PAID' | 'REDEEMED';
  startDate: string;
  endDate: string;
  duration?: string;
  subType?:
    | 'COMP'
    | 'FACTORY'
    | 'MODEL_YEAR'
    | 'MODEL'
    | 'MAKE'
    | 'RPO'
    | 'REDEEMED'
    | 'REDEEMABLE';
  licenseChangeRequest?: LicenseChangeRequest;
  link?: ProductLink;
};
export type AssetLicensesUpdateNotificationPayload = {
  assetType: 'VINS' | 'PERIPHERALS';
  assetId: string;
  organizationIdStr: string;
  licensedFeaturesBefore: string[];
  licensedFeaturesAfter: string[];
  licensedProductsBefore: ProductInfo[];
  licensedProductsAfter: ProductInfo[];
  timestamp: string;
};
export type ReprocessedLicensesDto = {
  updated?: LicenseDto[];
  failedAssets?: string[];
  updatesNotificationPayload?: AssetLicensesUpdateNotificationPayload[];
};
export type GmResponseCommonReprocessedLicensesDto = {
  result?: ReprocessedLicensesDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type ReprocessingDto = {
  /** The flow to be re-processed. */
  flow: 'ONBOARDING' | 'OFFBOARDING';
  /** A list of the entities to re-process by the entity type as a key.
            There should be at least one list (key) in the map. */
  assets: {
    [key: string]: string[];
  };
};
export type UpdatePaymentMethodDto = {
  /**
                Optional address to update.
                If addressLine1 is provided, all other address fields become required.
             */
  address?: AddressDto;
  /**
                Conditional. The expiration month of the card (1–12). If provided, expirationYear must also be provided.
             */
  expirationMonth?: number;
  /** Conditional. Required if expirationMonth is provided. The expiration year of the card. */
  expirationYear?: number;
};
export type UpdateLicenseDto = {
  /** Start date of the license to be updated */
  startDate?: string;
  /** Product ID of the license to be updated */
  productId?: string;
  /** The id of the licensing model from the product catalog. */
  licensingModelId?: string;
  /** Expiration date of the license to be updated */
  expirationDate?: string;
  /** The license status to be updated. */
  status?:
    | 'PENDING'
    | 'ACTIVE'
    | 'SUSPENDED'
    | 'TERMINATED'
    | 'EXPIRED'
    | 'REDEEMABLE'
    | 'REDEEMED';
  /** The payment status to be updated. */
  paymentStatus?: 'PENDING' | 'COMPLETE' | 'FAILED' | 'NOT_REQUIRED';
  /** Enrolled entities by asset type */
  enrolledEntities?: {
    [key: string]: EnrolledEntitiesDto;
  };
  /** Flag to indicate if to override scheduled with what is set in this model */
  overrideScheduled: boolean;
  /** The scheduled attribute to be updated with. */
  scheduled?: {
    [key: string]: {
      [key: string]: ScheduledDto;
    };
  };
};
export type QuickpayPaymentResponse = {
  /** The payment identifier from the external payment service. */
  id: string;
  /** The payment number from the external payment service. */
  number: string;
  /** The payment amount. */
  amount: number;
  /** The currency of the payment amount. */
  currency: string;
};
export type GmResponseCommonQuickpayPaymentResponse = {
  result?: QuickpayPaymentResponse;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type SearchAccountInvoiceResponse = {
  /** Total invoice amount. */
  amount: number;
  /** Outstanding balance amount. */
  balance: number;
  /** Unique invoice record identifier. */
  id: string;
  /** Invoice number. */
  invoiceNumber: string;
  /** Customer account number. */
  accountNumber: string;
  /** Unique account identifier. */
  accountId: string;
  /** Invoice due date. */
  dueDate: string;
  /** Invoice currency. */
  currency: string;
  /** Current invoice payment status. */
  status: 'DUE' | 'OVERDUE' | 'PAID';
};
export type GmResponseCommonSearchAccountInvoiceResponse = {
  result?: SearchAccountInvoiceResponse;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type DiscountHistoryDto = {
  /** Discounts attached to the subscription across its lifetime. */
  discounts: DiscountDto[];
};
export type GmResponseCommonDiscountHistoryDto = {
  result?: DiscountHistoryDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type GmResponseCommonListDiscountDto = {
  result?: DiscountDto[];
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type EligibleAssetsByPricingModelDto = {
  /** List of the eligible asset identifiers. */
  entityIds: string[];
  /** The type of the Asset type that is eligible for the pricing. */
  type: 'VINS' | 'PERIPHERALS';
  /** Identifier for the Pricing information. */
  pricingId: string;
};
export type GmResponseCommonListEligibleAssetsByPricingModelDto = {
  result?: EligibleAssetsByPricingModelDto[];
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type GmResponseCommonListString = {
  result?: string[];
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type ConditionDto = {
  type:
    | 'SUBSCRIPTION'
    | 'RPO'
    | 'MODEL_YEAR'
    | 'MAKE'
    | 'MODEL'
    | 'VEHICLE_CAPABILITY'
    | 'TRIAL'
    | 'ENTITLEMENT_PRODUCT'
    | 'ENTITLEMENT_CAPABILITY'
    | 'ORED'
    | 'ANDED'
    | 'ASSET_TYPE'
    | 'PERIPHERAL_TYPE'
    | 'ASSOCIATION'
    | 'REDEEMED'
    | 'REDEEMABLE'
    | 'NEVER'
    | 'PREPAID'
    | 'ACTIVATION'
    | 'ON_STAR_STATUS'
    | 'ON_STAR_SUPPORT'
    | 'ENROLLMENT_DATE';
  negated: boolean;
  conditionCode: string;
};
export type AcceptedValuesConditionDto = ConditionDto & {
  acceptedValues: string[];
};
export type AssociationConditionDto = ConditionDto & {
  acceptedValues: string[];
};
export type BooleanValuesConditionDto = ConditionDto;
export type EnrollmentDateConditionDto = ConditionDto & {
  start?: string;
  end?: string;
};
export type EntitlementCapabilityConditionDto = ConditionDto & {
  requiredValues: string[];
};
export type EntitlementProductConditionDto = ConditionDto & {
  acceptedValues: string[];
  trial?: 'ALLOWED' | 'BLOCKED';
};
export type ModelYearConditionDto = ConditionDto & {
  start?: number;
  end?: number;
};
export type NeverConditionDto = ConditionDto;
export type SubscriptionConditionDto = ConditionDto;
export type VehicleCapabilityConditionDto = ConditionDto & {
  name: string;
  value: string;
};
export type AggregatedConditionDto = ConditionDto & {
  conditions: (
    | AcceptedValuesConditionDto
    | AggregatedConditionDto
    | AssociationConditionDto
    | BooleanValuesConditionDto
    | EnrollmentDateConditionDto
    | EntitlementCapabilityConditionDto
    | EntitlementProductConditionDto
    | ModelYearConditionDto
    | NeverConditionDto
    | SubscriptionConditionDto
    | VehicleCapabilityConditionDto
  )[];
};
export type PricingModelDto = {
  /** Identifier for the Pricing information. */
  id: string;
  /** Price of the Product Unit */
  price: number;
  /** Zuora rate plan Id for this price */
  ratePlanId: string;
  /** Zuora rate plan charge Id for this price */
  ratePlanChargeId: string;
  /**
                Defines the information that will be used to match a specific asset to the correct pricing model.
                All conditions must match the asset in order to use the pricing model (i.e. they use AND operator).
                When no conditions are provided, any asset will match for the given price.
             */
  attributeConditions: (
    | AcceptedValuesConditionDto
    | AggregatedConditionDto
    | AssociationConditionDto
    | BooleanValuesConditionDto
    | EnrollmentDateConditionDto
    | EntitlementCapabilityConditionDto
    | EntitlementProductConditionDto
    | ModelYearConditionDto
    | NeverConditionDto
    | SubscriptionConditionDto
    | VehicleCapabilityConditionDto
  )[];
};
export type LicensingModelDto = {
  /** Identifier for the Licensing information. */
  id: string;
  type:
    | 'SUBSCRIPTION'
    | 'RPO'
    | 'MODEL_YEAR'
    | 'MAKE'
    | 'MODEL'
    | 'TRIAL'
    | 'HARDWARE'
    | 'REDEEMABLE';
  duration?: string;
  dates: 'CURRENT' | 'DELIVERY' | 'ONSTAR_ENROLLMENT' | 'GM_ACCOUNT_CREATION';
  visibleTo: ('ADVISOR' | 'CUSTOMER')[];
  maxCapacity?: number;
  licensingCondition?:
    | AcceptedValuesConditionDto
    | AggregatedConditionDto
    | AssociationConditionDto
    | BooleanValuesConditionDto
    | EnrollmentDateConditionDto
    | EntitlementCapabilityConditionDto
    | EntitlementProductConditionDto
    | ModelYearConditionDto
    | NeverConditionDto
    | SubscriptionConditionDto
    | VehicleCapabilityConditionDto;
  additionalConditions?: (
    | AcceptedValuesConditionDto
    | AggregatedConditionDto
    | AssociationConditionDto
    | BooleanValuesConditionDto
    | EnrollmentDateConditionDto
    | EntitlementCapabilityConditionDto
    | EntitlementProductConditionDto
    | ModelYearConditionDto
    | NeverConditionDto
    | SubscriptionConditionDto
    | VehicleCapabilityConditionDto
  )[];
  usageRestrictions?: UsageRestrictionDto[];
  allowedEnrollmentOperations?: ('REMOVE' | 'ADD' | 'REPLACE')[];
  /**
            Zuora Rate Plan.
            Deprecated since 2026-03-01. Use pricingModels instead.
             */
  ratePlanId?: string;
  /**
            Pricing information for the Rate Plan.
            Deprecated since 2026-03-01. Use pricingModels instead.
             */
  pricingModel?: PricingModelDto;
  /**
                A set of pricing models to support attribute based pricing,
                by matching up a price according to asset attributes.
             */
  pricingModels: PricingModelDto[];
  annualSavingsPercentage?: number;
  /** Allowed discount percentages for this licensing model. Null if discounts are not applicable. */
  allowedDiscountPercents?: number[];
};
export type FeatureDto = {
  id: string;
  code: string;
  name: string;
  description?: string;
  createdDateTime: string;
  createdBy: string;
  updatedDateTime: string;
  updatedBy: string;
  capabilityChecks?: (
    | AcceptedValuesConditionDto
    | AggregatedConditionDto
    | AssociationConditionDto
    | BooleanValuesConditionDto
    | EnrollmentDateConditionDto
    | EntitlementCapabilityConditionDto
    | EntitlementProductConditionDto
    | ModelYearConditionDto
    | NeverConditionDto
    | SubscriptionConditionDto
    | VehicleCapabilityConditionDto
  )[];
};
export type ProductPricingDto = {
  duration: string;
  price: number;
  annualSavingsPercentage?: number;
  /** Allowed discount percentages for this billing duration. Null if discounts are not applicable. */
  allowedDiscountPercents?: number[];
};
export type RequiredProductDto = {
  code: string;
  name: string;
  pricing: ProductPricingDto[];
};
export type AddsOnProductDto = {
  code: string;
  name: string;
  price: number;
};
export type ProductDto = {
  id: string;
  zuoraProductId?: string;
  code: string;
  name: string;
  description?: string;
  effectiveDate: string;
  sunsetDate?: string;
  sunsetBy?: string;
  constraintConditions?: (
    | AcceptedValuesConditionDto
    | AggregatedConditionDto
    | AssociationConditionDto
    | BooleanValuesConditionDto
    | EnrollmentDateConditionDto
    | EntitlementCapabilityConditionDto
    | EntitlementProductConditionDto
    | ModelYearConditionDto
    | NeverConditionDto
    | SubscriptionConditionDto
    | VehicleCapabilityConditionDto
  )[];
  licensingModels: LicensingModelDto[];
  countries: string[];
  /** Features this product can entitle. Sourced from product catalog; may be empty for HARDWARE catalog products. Non-HARDWARE catalog products are required to list at least one feature. */
  features: FeatureDto[];
  createdDateTime: string;
  createdBy: string;
  updatedDateTime: string;
  updatedBy: string;
  productType?: 'HARDWARE' | 'SERVICE';
  productGroup?: string;
  requiredProducts?: RequiredProductDto[];
  addsOnProducts?: AddsOnProductDto[];
};
export type SubscriptionLicenseDto = {
  /** Subscription term duration. */
  duration: string;
  /** The id of the licensing model of the licensed product */
  licensingModelId: string;
  /** LicensingModel index of the licensed product. */
  licensingModelIndex: number;
  /** Subscription license expiration date, for renewing subscription it's null. */
  expirationDate?: string;
  /** Next billing cycle date. */
  nextBcdDate: string;
  /** Zuora subscription number for the licensed subscription. Populated for active paid organization-level subscriptions regardless of whether a discount is applied. Null for trial licenses and for licenses with no Zuora subscription. This is the value accepted by the apply post-sales discount endpoint. */
  subscriptionNumber?: string;
};
export type ProductSubscriptionDetailsDto = {
  /**
                Product with all the attributes returned by the Product Catalog, including licensing models
                that carry pricing information for SUBSCRIPTION model types.
                Any other licensing models that are not customer-facing are not returned.
             */
  product: ProductDto;
  /**
                List containing invoice details. For renewable subscriptions the list will always contain 2 elements –
                one is for the amount charged today (could be prorated subscription) and the full amount charged
                the next cycle, both elements contain start/end dates for the respective billing cycles.

                For details see response from Preview Order.
             */
  invoiceItemSummary?: InvoiceItemSummaryDto[];
  /**
                Details on the payment method used for recurring charges on the subscription.
                For details see paymentMethods attribute of the Billing Account model.
             */
  paymentMethod?: PaymentMethodDto;
  /**
                Assets counts with their relationship to the product. The attribute is a map with the predefined keys:
                ENROLLED, ELIGIBLE, ASSIGNABLE, RPO, TRIAL, UNENROLLED.

                Note that some of those categories are not orthogonal, so the sum may not equal the total number of assets.
                Not all categories may be present in the response, indicating a count of 0 in that category.

                For details on the count breakdown see Single Product Eligibility Summary response description.
             */
  counts: {
    [key: string]: number;
  };
  /** Subscription license information including duration */
  subscriptionLicense?: SubscriptionLicenseDto;
  /** Discount in effect for this product, display-only: the one reducing the subscription's bill, or an unlinked plan discount waiting for its subscription to exist, which reduces nothing yet. Null when there is none. */
  discount?: DiscountSummaryDto;
  /** Full discount history for the subscription across all statuses (ACTIVE, CANCELLED, REPLACED, EXPIRED), ordered by createdAt descending. Empty when the subscription has never had a discount. The discount field above stays for backward compatibility; this array is additive. */
  discountHistory: DiscountSummaryDto[];
};
export type GmResponseCommonProductSubscriptionDetailsDto = {
  result?: ProductSubscriptionDetailsDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type RequiredProductSubscriptionLicenseDto = {
  code: string;
  name: string;
  subscriptionLicense?: SubscriptionLicenseDto;
};
export type ProductEligibilitySummaryDto = {
  /** The product details */
  product: ProductDto;
  /** Eligibility and entitlement counts for the product */
  counts: {
    [key: string]: number;
  };
  /** Paid organization level Subscription license information including duration */
  subscriptionLicense?: SubscriptionLicenseDto;
  /** Trial asset level Subscription license information including duration */
  trialLicense?: SubscriptionLicenseDto;
  /** Earliest expiration date among active trial licenses not set for conversion. Null when the product has no active unconverted trials for this organization. */
  earliestTrialExpiration?: string;
  /** Discount in effect for this product, display-only: the one reducing the subscription's bill, or an unlinked plan discount waiting for its subscription to exist, which reduces nothing yet. Null when there is none. */
  discount?: DiscountSummaryDto;
  /** Subscription license data for required products. Null when requiredProducts is absent; each entry's subscriptionLicense is null when not yet subscribed. */
  requiredProductSubscriptionLicense?: RequiredProductSubscriptionLicenseDto[];
};
export type GmResponseCommonListProductEligibilitySummaryDto = {
  result?: ProductEligibilitySummaryDto[];
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type GmResponseCommonProductEligibilitySummaryDto = {
  result?: ProductEligibilitySummaryDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type EntitledProductDto = {
  /** Name of the entitled product. */
  name: string;
  /** Code of the entitled product. */
  code: string;
  /** Entitled product licenses level, which is a combination of all licenses levels for the product. */
  levels: ('ORGANIZATION' | 'ASSET')[];
  /** List of entitled features that are provided under this product. */
  features: string[];
  /** Expiration date of the entitlement for the product which is max of all licenses expirations for the product. */
  maxExpiration: string;
};
export type EntitlementDto = {
  product: EntitledProductDto;
};
export type EntitlementResponseDto = {
  /** Set of the entitled products which includes both org level entitlements and asset level entitlements. */
  entitlements: EntitlementDto[];
  /** Asset level enablements, which is a combination of asset entitlements, based both on directasset licences and those were assets are enrolled under org licenses, and asset capabilities. */
  enablements: EnablementsDto;
};
export type GmResponseCommonEntitlementResponseDto = {
  result?: EntitlementResponseDto;
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export type LicenseSummaryDto = {
  /** Echoed back organization ID from the request */
  organizationId: string;
  /** Name of the product the summary is about */
  productName: string;
  /** ID of the product the summary refers to */
  productId: string;
  /** Well known product code for the summary element */
  productCode: string;
  /** License level (ORGANIZATION or ASSET) the summary is for */
  licenseLevel: string;
  /** The type of asset that the summary covers (VINS or PERIPHERALS) */
  assetType: string;
  /**
                Total number of enrolled entities for this product, license level, and asset type.
                 */
  enrolledEntityCount: number;
};
export type GmResponseCommonListLicenseSummaryDto = {
  result?: LicenseSummaryDto[];
  targetUrl: string;
  __bd: boolean;
  success: boolean;
  unauthorizedRequest: boolean;
  errors: GmResponseError[];
  traceId?: string;
};
export const {
  useUpdateShippingAddressMutation,
  useCreatePaymentSessionMutation,
  useSubmitQuickpayEmailMutation,
  useCreateShippingAddressMutation,
  useListOrdersQuery,
  usePlaceOrderMutation,
  usePreviewPeripheralOrderQuery,
  useRedeemLicenseMutation,
  useListInvoicesMutation,
  useSyncOviSubscriptionsMutation,
  useValidateMigratedSubscriptionsMutation,
  useUpdateZuoraAccountMutation,
  useValidateZuoraAccountMutation,
  useFindMigratedBillingAccountsQuery,
  useSyncOviBillingAccountMutation,
  useValidateMigratedOviBillingAccountMutation,
  useUpdateInvoiceMeEnabledIfAllowedMutation,
  useApplyDiscountToSubscriptionMutation,
  useListDiscountsMutation,
  useExportDiscountsMutation,
  useHandleZuoraCalloutMutation,
  useHandleInvoicePostedMutation,
  useCreatePaymentSession1Mutation,
  useAddPaymentMethodMutation,
  useGetInvoiceHistoryQuery,
  useDownloadInvoicesMutation,
  useCreateOrderMutation,
  useUnenrollAssetsMutation,
  usePreviewOrderQuery,
  useEnrollAssetsMutation,
  usePreviewEnrollAssetsQuery,
  useExecuteActionsMutation,
  useCloneAndOnboardMutation,
  useGetOrganizationAssetsWithPriceQuery,
  useGetOrganizationAssetsQuery,
  useGetLicensesQuery,
  useCreateLicenseMutation,
  useGetOrganizationEnablementsQuery,
  useReprocessLicensesForAssetsMutation,
  useCancelDiscountMutation,
  useChangeDiscountMutation,
  useSetDefaultPaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useUpdateLicenseMutation,
  useGetPaymentDetailsQuery,
  useSearchAccountInvoiceQuery,
  useGetDiscountHistoryQuery,
  useGetDiscountsByOrganizationQuery,
  useGetOrganizationBillingAccountQuery,
  useGetEligibleAssetIdsGroupedByPricingIdQuery,
  useGetEligibleAssetIdsQuery,
  useGetProductSubscriptionDetailsQuery,
  useGetProductSubscriptionSummaryQuery,
  useGetSingleProductEligibilitySummaryQuery,
  useGetOrganizationEntitlementsQuery,
  useGetLicenseSummariesQuery,
  useDeletePaymentMethodMutation,
} = injectedRtkApi;
